import type { Translations } from "../index";

export const ko: Translations = {
  // ─── Language meta ───────────────────────────────────────────────────────
  lang: {
    en: "EN",
    ja: "日本語",
    "zh-HK": "繁中",
    "zh-TW": "繁中",
    "zh-CN": "简中",
    ru: "Русский",
    ko: "한국어",
    th: "ไทย",
    current: "한국어",
  } as Translations["lang"],

  // ─── Global nav ──────────────────────────────────────────────────────────
  nav: {
    platform: "플랫폼",
    forDesigners: "디자이너",
    forBrands: "브랜드",
    forManufacturers: "프로덕션",
    forStudio: "Studio",
    caraAi: "Cara AI",
    pricing: "요금제",
    more: "더 보기",
    about: "소개",
    academy: "아카데미",
    accessPlatform: "플랫폼으로 →",
    subline: "디자인과 프로덕션이 만나는 곳",
    sublineBrand: "소싱 · 생산 · 런칭",
    sublineDesigner: "주얼리 디자이너의 런웨이",
    inspire: "Inspire",
    newsroom: "뉴스룸",
    contact: "문의",
    catalog: "카탈로그",
    showcase: "쇼케이스",
    switchToDesigners: "디자이너 →",
    switchToBrands: "브랜드 →",
    howItWorks: "이용 방법",
    overview: "개요",
  },

  // ─── Footer ──────────────────────────────────────────────────────────────
  footer: {
    subline: "디자이너 주얼리 · B2B 소싱 · EPH 제조",
    tagline: "\"비할 데 없는 디자인과\n완벽한 프로덕션이 만나는 마켓플레이스.\"",
    accessPlatform: "플랫폼으로 →",
    ephCertified: "EPH 인증 제조",
    copyright: "Caratzon Ltd. All rights reserved.",
    columns: {
      platform: "플랫폼",
      company: "기업 정보",
      resources: "리소스",
      contact: "문의",
    },
    links: {
      forDesigners: "디자이너",
      forBrands: "브랜드",
      forManufacturers: "프로덕션",
      caraAi: "Cara AI",
      tradeCatalogue: "트레이드 카탈로그",
      accessPlatform: "플랫폼으로",
      aboutCaratzon: "Caratzon 소개",
      ourStory: "스토리",
      leadership: "경영진",
      pressMedia: "보도 · 미디어",
      investorRelations: "IR 정보",
      howItWorks: "이용 방법",
      ephHub: "EPH 제조 허브",
      sustainabilityReport: "지속가능성 보고서",
      cadStandards: "CAD 규격",
      supplierCode: "공급업체 행동 규범",
      academy: "아카데미",
      inspire: "Inspire",
      newsroom: "뉴스룸",
      aiCredits: "AI 크레딧",
      contactUs: "문의하기",
      becomeManufacturer: "제조 파트너 신청",
    },
    offices: {
      hongkong: "디자인 게이트웨이 · 香港",
      guangdong: "제조 허브 · 广东",
      london: "유럽 트레이드 데스크",
    },
    legal: {
      terms: "이용약관",
      privacy: "개인정보 처리방침",
      cookies: "쿠키 정책",
      supplierCode: "공급업체 규범",
    },
  },

  // ─── Home page ───────────────────────────────────────────────────────────
  home: {
    meta: {
      title: "Caratzon — B2B 주얼리 소싱 · 프리엔지니어드 CAD · AI 마케팅",
      description:
        "독립 파인 주얼리를 위한 프리미어 B2B 소싱 플랫폼. 프리엔지니어드 CAD 디자인을 라이선스하고, EPH에서 지속가능하게 제조하며, Caratzon Studio로 마케팅 에셋을 즉시 생성합니다. 3단계 IP 라이선스 체계, 엔드투엔드 생산 관리.",
    },
    emergence: {
      tagline: "스케치에서 매장까지. 디자인, 라이선스, 제조 — 오직 Caratzon.",
      subtitle: "주얼리 디자인, 라이선스, 생산을 위한 B2B 플랫폼",
      cta: "카탈로그 둘러보기",
      ctaSecondary: "이용 방법 보기",
    },
    process: {
      overline: "이용 방법",
      steps: [
        { number: "01", heading: "발견", body: "전 세계 독립 디자이너의 프리엔지니어드 오리지널을 브라우징합니다.", hook: "R&D 비용 없음. NRE 수수료 없음." },
        { number: "02", heading: "라이선스", body: "IP 티어를 선택하고 수 초 만에 상업적 권리를 취득합니다.", hook: "모든 라이선스에 마케팅 에셋 포함." },
        { number: "03", heading: "생산", body: "디자인이 EPH의 지속가능한 제조 허브로 바로 전달됩니다. 완료.", hook: "공장 관리 불필요. 완전한 추적성." },
      ],
    },
    storyBeat: {
      line1: "스케치에서 매장까지.",
      line2: "디자인, 라이선스, 제조.",
      line3: "오직 Caratzon.",
      splitLabel: "저는",
      splitDesigner: "디자이너",
      splitBrand: "브랜드 바이어",
    },
    showcase: {
      pieces: [
        {
          sku: "FD001-251225032",
          designer: "야위 스튜디오",
          name: "셀레스티얼 드레이프 네크리스",
          descriptor: "흐르는 금, 빛 속에 부유하다.",
        },
        {
          sku: "FD016-251224017",
          designer: "골든 후루",
          name: "복 호리병 펜던트",
          descriptor: "고대의 상징, 현대의 실루엣.",
        },
        {
          sku: "FD033-251225049",
          designer: "피오니 아틀리에",
          name: "블룸 드롭 이어링",
          descriptor: "꽃잎, 떨어지는 순간을 포착하다.",
        },
        {
          sku: "FD051-251226013",
          designer: "제이드 리버 디자인",
          name: "리플 커프 브레이슬릿",
          descriptor: "손목 위의 물결, 금으로 얼어붙다.",
        },
      ],
    },
    stats: [
      { value: "48+", numericValue: 48, label: "오리지널 디자인" },
      { value: "12", numericValue: 12, label: "독립 디자이너" },
      { value: "6", numericValue: 6, label: "개국" },
      { value: "80%", numericValue: 80, label: "디자이너 수익 배분", suffix: "%" },
    ],
    fork: {
      heading: "자, 당신은 어디에 속하시나요?",
      designer: {
        eyebrow: "디자이너",
        line: "당신의 기술은 세계 무대에 어울립니다 — 인스타그램 포스팅이 아니라.",
        cta: "디자이너로 입장",
      },
      brand: {
        eyebrow: "브랜드",
        line: "R&D를 건너뛰세요. 마케팅 에셋이 포함된 라이선스. 오늘 바로 런칭.",
        cta: "브랜드로 입장",
      },
    },
    close: {
      line: "다음 장(章)은 여기에서 시작됩니다.",
      cta: "시작하기",
    },
    hero: {
      overline: "B2B 소싱 플랫폼 · EPH를 통한 지속가능한 프로덕션",
      h1Line1: "디자인을 소싱하세요.",
      h1Line2: "R&D를 건너뛰세요.",
      h1Line3: "즉시 런칭하세요.",
      tagline:
        "독립 파인 주얼리를 위한 프리미어 B2B 소싱 플랫폼. 프리엔지니어드 CAD 디자인을 라이선스하고, EPH 허브에서 지속가능하게 제조합니다. AI Studio가 '구매' 클릭 순간 마케팅 에셋을 자동 생성합니다.",
      cta: "트레이드 카탈로그 보기",
      ctaSecondary: "Caratzon Studio 알아보기",
    },
    pillars: {
      overline: "플랫폼 역량",
      subtitle: "엔드투엔드 워크플로",
      items: [
        {
          heading: "AI 마케팅 엔진 (Caratzon Studio)",
          body: "샘플 촬영을 위해 수 주간 기다릴 필요가 없습니다. 모든 라이선스에 Essentials Kit(이커머스 촬영·스펙·SEO 카피)가 기본 포함됩니다. Caratzon Kit으로 업그레이드하면 라이프스타일 포토그래피, SNS 에셋, 브랜드 컬러에 맞춘 PDF 라인시트를 자동 생성합니다.",
        },
        {
          heading: "3단계 IP 라이선스, 법적으로 문서화",
          body: "스탠다드(비독점), 시즈널 엠바고(12개월 독점), 풀 Vault IP(영구 IP 양도). 법적 구속력 있는 상업적 권리 — 단순 악수가 아닙니다.",
        },
        {
          heading: "엔드투엔드 EPH 프로덕션",
          body: "주문이 검증된 EPH 제조 네트워크로 직접 라우팅됩니다. 캐스팅·세팅·QC·물류를 하나의 통합형 지속가능 허브에서 관리합니다. 공장 협상이 필요 없습니다.",
        },
      ],
    },
    quote: {
      text: "\"다음 컬렉션 — R&D 없이, 에이전시 없이, 관리할 공장 없이.\n소싱부터 진열까지, 하나의 플랫폼.\"",
      attribution: "Caratzon의 신념",
    },
    marquee: {
      ariaLabel: "카탈로그 미리보기",
    },
    howItWorks: {
      overline: "이용 방법",
      subtitle: "3단계",
      items: [
        {
          heading: "발견",
          body: "독립 크리에이터의 프리엔지니어드 오리지널을 브라우징합니다. 각 피스의 Essentials Kit를 미리 확인하세요 — 카탈로그 촬영, 스펙, 에디토리얼 스토리, 인터랙티브 3D 뷰어 — 커밋 전에 전체 패키지를 평가할 수 있습니다.",
        },
        {
          heading: "라이선스 & 생성",
          body: "IP 티어(스탠다드, 엠바고 또는 Vault)를 선택하고 수 초 만에 상업적 권리를 취득합니다. 라이선스가 확정되는 순간, Caratzon Studio가 완전한 마케팅 키트를 생성하여 영업팀이 즉시 활동을 시작할 수 있습니다.",
        },
        {
          heading: "EPH를 통해 생산",
          body: "승인된 디자인이 EPH의 통합 시설로 라우팅됩니다. 지속가능한 전기 도금, 인증된 친환경 공정, 엔드투엔드 마일스톤 추적 — 마케팅 에셋은 이미 라이브 상태입니다.",
        },
      ],
    },
    eph: {
      overline: "제조 파트너",
      location: "중국",
      headline: "중국 최대 지속가능 통합형 주얼리 & 친환경 도금 제조 허브",
      body: "Caratzon을 통한 모든 생산 주문은 EPH의 통합 시설에서 이행됩니다 — 캐스팅, 세팅, 폴리싱, 친환경 도금을 한 지붕 아래에서, 완전한 환경 규정 준수와 마일스톤 추적성을 구현합니다.",
      attrs: [
        {
          label: "지속가능한 전기 도금",
          body: "저배출 친환경 도금 라인 — 골드, 로듐, 합금 피니시. 화학 폐기물 저감 및 폐쇄 순환 수처리 시스템 검증 완료.",
        },
        {
          label: "통합형 시설",
          body: "캐스팅·세팅·폴리싱·도금을 한 지붕 아래에서. 핸드오프 감소, 엄격한 공차 관리, 단축된 사이클 타임.",
        },
        {
          label: "다이렉트 통합",
          body: "Caratzon에서 승인된 디자인이 EPH의 생산 대기열로 자동 라우팅됩니다. 수동 재소싱이나 단절된 공급망이 발생하지 않습니다.",
        },
        {
          label: "완전한 추적성",
          body: "모든 생산 단계가 기록되어 Caratzon 대시보드에서 확인 가능 — 원자재 입고부터 최종 QC 승인까지.",
        },
      ],
    },
    forWhom: {
      overline: "대상 고객",
      heading: "트레이드 바이어를\n위해 구축되었습니다.",
      items: [
        {
          title: "주얼리 리테일러",
          desc: "경쟁사가 구할 수 없는 디자이너 다이렉트 피스를 소싱하세요. 모든 라이선스에 Essentials Kit 기본 포함 — 카탈로그 촬영·스펙·SEO 카피 즉시 제공. Caratzon Kit으로 업그레이드하면 라이프스타일 포토그래피 & SNS 에셋도 가능. 라이선스 취득 당일에 웹사이트 게시·카탈로그 수록·인스타그램 포스팅이 가능합니다.",
        },
        {
          title: "부티크 오너",
          desc: "디자인당 $1,500 이상의 NRE(초기 개발비)를 건너뛰세요. 테스트 완료, 생산 준비된 오리지널을 라이선스합니다. Caratzon Kit을 생성하여 라이선스 계약 당일 Shopify에 신상품을 게시하세요.",
        },
        {
          title: "멀티브랜드 유통사",
          desc: "하나의 벤더 프로필로 수십 명의 글로벌 독립 디자이너로부터 소싱합니다. 완벽하게 포맷된 PDF 도매 라인시트를 자동 생성하여 리테일 거래처에 즉시 공유하세요.",
        },
      ],
    },
    caraAI: {
      overline: "Cara AI · 엔지니어링 인텔리전스",
      heading: "공장 현장을 위해\n설계하는 AI.",
      body: "Cara는 판타지 렌더링을 생성하지 않습니다. 로스트 왁스 캐스팅, 스톤 세팅에 견디고 하루 종일 착용해도 편안한 주얼리를 엔지니어링합니다 — 커피를 다 마시기 전에 완전한 부품표까지 생성합니다.",
      stats: [
        { value: "±0.15mm", label: "합금별 수축 보정" },
        { value: "0.6mm", label: "최소 벽두께 적용" },
        { value: "4-Prong+", label: "스톤 고정 엔지니어링" },
        { value: "R≥0.4mm", label: "종일 착용 편안한 에지 반경" },
      ],
      cta: "Cara AI 체험하기 — 라이브 데모",
      ctaSecondary: "자세히 보기",
    },
    inspirePreview: {
      overline: "Inspire에서 발견",
      heading: "스크롤. 저장. 소싱.",
      body: "Caratzon Inspire에 오신 것을 환영합니다 — B2B 디스커버리 피드. 세계 최고 수준의 3D CAD 디자인 큐레이션 스트림을 브라우징하세요. 브랜드 무드보드를 구성하고 좋아하는 독립 크리에이터를 팔로우하세요. 인스피레이션에서 프로덕션까지 원클릭으로 전환합니다.",
      cta: "Inspire 탐색",
    },
    designerBanner: {
      overline: "디자이너",
      heading: "당신의 CAD 파일에는\n더 나은 포트폴리오가 필요합니다.",
      body: "글로벌 3D 크리에이터 네트워크에 참여하세요. 프리엔지니어드 디자인을 업로드하면 프리미엄 Caratzon Inspire 포트폴리오가 자동으로 언락됩니다. AI가 무료로 렌더링하고, 수천 명의 인증된 리테일 바이어에게 작품을 쇼케이스하며, 높은 마진의 로열티를 획득할 수 있습니다.",
      cta: "디자이너 포트폴리오 신청",
    },
    footerCta: {
      overline: "소싱 시작",
      heading: "다음 컬렉션.\n하나의 플랫폼. 빈틈 없이.",
      cta: "트레이드 카탈로그 보기",
      tradeOnly: "트레이드 계정 전용",
    },
  },

  // ─── For Designers page ──────────────────────────────────────────────────
  forDesigners: {
    meta: {
      title: "디자이너 · Caratzon",
      description:
        "당신의 스토리를 AI가 전합니다. 당신의 디자인이 전 세계에 소개됩니다. Caratzon Inspire에 참여하세요 — CAD를 업로드하고 크래프트에 관한 몇 가지 질문에 답하면 AI가 디자이너 스토리를 작성합니다. 라이선스 수수료의 80%와 영구 로열티를 획득하세요.",
    },
    hero: {
      overline: "독립 주얼리 디자이너를 위해",
      h1Line1: "당신의 이야기를.",
      h1Line2: "아름답게, 세계로.",
      tagline:
        "당신의 크래프트, 영감, 비전에 관한 몇 가지 질문에 답해 주세요. AI가 그 답변을 매력적인 디자이너 내러티브로 변환하여 전 세계 바이어에게 전달합니다.",
      cta: "인스피레이션이 되세요 →",
    },
    stats: {
      share: "라이선스 수수료 중 디자이너 몫",
      ipProtected: "IP 완전 보호",
      cadNote: "CAD 데이터는 바이어에게 공개되지 않습니다",
      perpetual: "영구 로열티",
      royaltiesNote: "제조 유닛당 발생",
      zeroMarketing: "마케팅 부담 제로",
      portfolioNote: "AI가 스토리를 구축합니다",
    },
    pain: {
      overline: "현실의 문제",
      p1: "당신은 재능 있는 주얼리 디자이너입니다. 작품은 독창적이고, 아름답게 완성되었으며, 생산 준비가 되어 있습니다.",
      p2: "그러나 생계를 위해 SNS 관리자, 카피라이터, 라이프스타일 포토그래퍼, 공장 중개인 역할까지 수행해야 합니다. 최고의 작품이 하드 드라이브에 잠들어 있습니다 — 글로벌 B2B 브랜드에 알릴 시간도 자본도 없기 때문입니다.",
      quote: "세계에서 가장 재능 있는 주얼리 디자이너가 가장 잘하는 일 — 디자인 — 에 집중할 수 있도록. 이것이 Caratzon의 존재 이유입니다.",
      quoteAttribution: "— Caratzon 창업팀",
    },
    inspire: {
      overline: "인스피레이션이 되세요",
      subtitle: "AI가 엮어내는 당신만의 스토리.",
      heading: "일곱 가지 질문.\n잊을 수 없는 스토리.",
      p1: "온보딩 과정에서 당신의 원점, 철학, 영감, 작품 뒤의 감성 세계에 대해 여쭙습니다. 양식 작성이 아닙니다 — 크리에이터로서의 당신 자신을 이야기하는 대화입니다.",
      p2: "AI가 그 답변을 바탕으로 매거진 퀄리티의 디자이너 프로필을 제작합니다 — 헤드라인, 내러티브, 테마별 섹션, 브랜드 보이스 키워드. 바이어가 스크롤을 멈추고 연락하고 싶어지는 스토리입니다.",
      previewLabel: "AI 생성 미리보기",
      previewTitle: "텐션 — Aria의 세계",
      previewBody: "그녀는 건축가가 빛을 생각하듯 주얼리를 디자인합니다 — 장식하기 위해서가 아니라 드러내기 위해.",
      previewExtended:
        "보스포루스 해협이 내려다보이는 햇살 가득한 스튜디오에서, Aria Atelier는 정밀함과 야생적 감각의 교차점에서 작업합니다. 그녀의 작품은 질감 있는 종이 위의 수채화 스케치에서 시작됩니다 — 화면을 터치하기 전에 무게를 느껴야 하니까요…",
      previewKeywords: ["텐션", "골드", "건축적", "와비사비", "조용한 자신감", "보스포루스의 빛"],
      cta: "인스피레이션이 되세요 →",
      features: [
        {
          label: "당신의 원점, 당신의 목소리",
          description: "디자이너가 된 계기, 끌리는 소재, 미학을 형성하는 문화적 배경. 당신의 말이 공개 스토리의 기반이 됩니다.",
        },
        {
          label: "철학과 내면 세계",
          description: "당신의 주얼리를 착용한 사람이 무엇을 느꼈으면 하는지. 작품을 정의하는 크리에이티브 텐션. 이러한 깊은 질문이 템플릿이 아닌 '당신다움'을 AI에 부여합니다.",
        },
        {
          label: "Inspire에서 세계로 공개",
          description: "AI가 생성한 스토리가 Inspire 크리에이터 페이지에 공개됩니다. 바이어, 프레스, 콜라보레이터가 발견할 수 있는 퍼블릭 프로필입니다. Studio 대시보드에서 언제든 편집 가능합니다.",
        },
      ],
    },
    designOfWeek: {
      overline: "Design of the Week",
      subtitle: "매주 선정. 전 세계에 소개.",
      heading: "Design of the Week에\n선정되세요.",
      badgeLabel: "이번 주의 디자인",
      featuredTitle: "프리즈매틱 페탈스 링",
      featuredAttribution: "by Aria Atelier · 2026년 3월 3일 피처",
      p1: "매주 Caratzon 에디토리얼 팀이 하나의 뛰어난 디자인을 선정하여 플랫폼 전체에서 스포트라이트를 비춥니다. 선정된 디자이너는 Inspire 홈페이지 프라임 포지션, 전용 에디토리얼 특집, 바이어 네트워크 전체에 대한 직접 노출을 획득합니다.",
      p2: "Design of the Week는 단순 노출이 아닙니다 — 업계 최고의 큐레이터에게 인정받았다는 신호입니다. 과거 선정에서 라이선스 계약, 언론 보도, 장기 브랜드 파트너십으로 이어진 사례가 있습니다.",
      benefits: [
        {
          label: "홈페이지 스포트라이트",
          description: "7일간 Inspire 홈페이지에서 가장 눈에 띄는 위치에 디자인과 스토리를 게시합니다. 바이어가 플랫폼을 방문할 때 가장 먼저 보는 곳입니다.",
        },
        {
          label: "에디토리얼 특집",
          description: "AI 생성 스토리를 기반으로 제작되는 전용 에디토리얼 기사. 디자인의 영감, 소재 선택, 제조 접근법에 대한 깊은 맥락을 추가합니다.",
        },
        {
          label: "바이어 네트워크 알림",
          description: "플랫폼 상의 모든 활성 바이어에게 당신의 디자인을 특집한 큐레이션 알림을 전송합니다. 라이선스 협상으로의 직접 파이프라인.",
        },
      ],
    },
    platform: {
      overline: "플랫폼의 작동 방식",
      subtitle: "수고 제로. 도달 극대화.",
      items: [
        {
          heading: "AI가 스토리를 전합니다",
          body: "온보딩에서 크래프트에 관한 질문에 답하시면 됩니다. AI가 디자이너 내러티브를 작성하고, 브랜드 보이스 키워드를 생성하고, Inspire 크리에이터 페이지를 공개합니다 — 작품과 함께 진화하는 라이브 포트폴리오입니다.",
        },
        {
          heading: "글로벌 바이어에 대한 직접 접근",
          body: "자동 생성된 포트폴리오가 적극적으로 소싱을 진행하는 대형 리테일 브랜드의 바이어와 생산 관리자에게 도달합니다. 영업 활동이 필요 없습니다 — 큐레이션된 쇼케이스를 통해 바이어가 당신을 발견합니다.",
        },
        {
          heading: "제조 번거로움 제로",
          body: "브랜드가 디자인을 라이선스해도 공장과의 소통은 일절 필요하지 않습니다. 플랫폼이 파일을 엄선된 제조 파트너에게 안전하게 라우팅합니다. 캐스팅·QC·물류는 모두 Caratzon이 처리합니다.",
        },
        {
          heading: "철벽 IP 보호",
          body: "CAD 파일은 어떤 라이선스 유형에서도 바이어에게 공개되지 않습니다. 브랜드가 취득하는 것은 생산 권리뿐입니다. 소스 파일은 NDA 하에 엄선된 제조 파트너에게만 제공됩니다. 디자인이 양도되는 것은 완전 IP 취득의 경우에 한합니다.",
        },
      ],
    },
    collections: {
      overline: "컬렉션 + AI 스토리텔링",
      subtitle: "당신의 디자인은 그리드 이상의 가치가 있습니다.",
      heading: "작품을 그룹핑.\nAI가 스토리를 엮습니다.",
      p1: "2개 이상의 공개된 디자인을 선택하면 AI가 공통점 — 공유 소재, 무드, 크리에이티브 감성 — 을 찾아내고 그 축으로 완전한 프레젠테이션을 구성합니다.",
      p2: "브랜드는 개별 피스뿐 아니라, 일관된 크리에이티브 비전과 통일감 있는 작품군을 가진 디자이너로 인식합니다. 이것이 연락으로 이어지는 이유입니다.",
      features: [
        {
          label: "모든 작품에 일관된 보이스",
          description: "AI가 디자인 전체를 관통하는 크리에이티브 실 — 공유 팔레트, 무드, 영감 — 을 식별합니다. 바이어는 무작위 파일 그리드가 아닌 일관된 비전을 봅니다.",
        },
        {
          label: "작품이 하나의 이야기를 전합니다",
          description: "각 디자인의 이름을 명시하고 그 사이의 연결을 그려내는 통합 내러티브. 바이어는 문의 전에 컬렉션 뒤의 사고를 이해합니다.",
        },
        {
          label: "모든 컬렉션이 보그 퀄리티",
          description: "통일된 에디토리얼 템플릿으로 매거진 퀄리티 쇼케이스 — 커버 이미지, 피스 스포트라이트, 모든 컬렉션에 걸친 일관된 비주얼 필.",
        },
      ],
      cta: "Studio에서 컬렉션 보기 →",
    },
    badges: {
      premium: "프리미엄",
    },
    revenue: {
      overline: "3가지 수익 모델",
      subtitle: "3가지 방법으로 수익을 얻습니다",
      heading: "브랜드는 3가지 방법으로 취득.\n당신은 3가지 방법으로 수익.",
      footnote:
        "3가지 모델 모두에서 라이선스 수수료의 80%를 획득합니다. Caratzon의 20%는 플랫폼 운영·마케팅·제조 코디네이션에 사용됩니다. 스탠다드 및 시즈널 엠바고에서는 제조 유닛당 영구 로열티가 발생합니다 — 초기 거래 이후에도 디자인이 지속적으로 수익을 창출합니다. 풀 독점권은 깔끔한 일시불 IP 양도입니다: 지속 로열티 없이, 당신이 설정한 최저 가격으로 프리미엄 일시불만 지급됩니다.",
      models: [
        {
          tier: "Standard",
          label: "비독점 프로덕션 라이선스",
          heading: "스탠다드 라이선스",
          description:
            "브랜드가 Caratzon의 제조 네트워크를 통해 디자인을 생산할 권리를 라이선스합니다. 비독점 — 여러 브랜드가 동시에 스탠다드 라이선스를 보유할 수 있습니다. 라이선스 수수료의 80%와 제조 유닛당 영구 로열티를 획득합니다.",
          benefit: "80%가 디자이너에게 · 영구 로열티 · 디자인은 마켓에 유지",
        },
        {
          tier: "Premium",
          label: "12개월 독점 제조 권리",
          heading: "시즈널 엠바고",
          description:
            "브랜드가 프리미엄을 지불하고 12개월간 디자인을 엠바고합니다. 캠페인 기간 동안 작품이 마켓플레이스에서 비공개 처리되어 경쟁사의 생산이 불가능합니다. 엠바고 수수료의 80%와 제조 유닛당 영구 로열티를 획득합니다. 기간 종료 후 디자인은 오픈 마켓으로 복귀합니다.",
          benefit: "80%가 디자이너에게 · 높은 선불금 · 기간 한정 · 반복 가능",
        },
        {
          tier: "Vault",
          label: "풀 독점권 (IP 양도)",
          heading: "풀 독점권",
          description:
            "브랜드가 영구적인 소유권을 취득하고 원본 생산 파일(.STL / .3DM)을 직접 수령합니다. 디자인은 마켓플레이스에서 영구적으로 삭제됩니다. 당신이 직접 설정한 일시불 프리미엄의 80%를 수령합니다. 깔끔한 엑시트: 1회 지급, 디자인 은퇴, 이후 의무 없음.",
          benefit: "80%가 디자이너에게 · 최저가를 디자이너가 설정 · 깔끔한 엑시트",
        },
      ],
    },
    steps: {
      overline: "온보딩에서 수익까지",
      subtitle: "3단계",
      items: [
        {
          heading: "인스피레이션이 되세요",
          body: "크래프트, 철학, 영감에 관한 질문에 답해 주세요. AI가 디자이너 내러티브를 생성하고 Inspire 페이지를 공개합니다 — 바이어가 발견하는 퍼블릭 크리에이터 프로필입니다. 첫 번째 CAD를 업로드하여 포트폴리오를 완성하세요.",
        },
        {
          heading: "AI가 브랜드를 구축합니다",
          body: "Story Lab이 스튜디오 퀄리티 룩북 촬영과 각 작품의 에디토리얼 내러티브를 생성합니다. 디자인을 컬렉션으로 그룹핑하면 AI가 통합 스토리를 작성합니다. Design of the Week에 선정되면 최대 노출을 획득합니다.",
        },
        {
          heading: "수익 획득",
          body: "브랜드가 당신의 작품을 발견하고 라이선스를 요청합니다. 승인 또는 카운터. 확인 후 생산이 자동 라우팅됩니다 — 1그램도 캐스팅되기 전에 라이선스 수수료의 80%가 확보되며, 제조 유닛당 로열티가 발생합니다.",
        },
      ],
    },
    bottomLine: {
      overline: "결론",
      p1: "당신의 일은 창작에 집중하는 것입니다. AI가 스토리를 전하고, 플랫폼이 작품을 판매합니다.",
      p2: "첫 온보딩 질문부터 첫 라이선스 계약까지 — 내러티브, 촬영, 바이어 디스커버리, 제조는 모두 Caratzon이 담당합니다. 당신은 디자인에만 전념하세요.",
      shareLabel: "당신의 몫",
      shareNote:
        "라이선스 수수료의 80%. 스탠다드 및 시즈널 엠바고에서는 유닛당 영구 로열티. 풀 독점권에서는 당신이 가격을 설정하는 깔끔한 일시불 프리미엄. Caratzon의 20%는 당신이 잠든 동안에도 작품을 판매하는 마케팅 엔진을 뒷받침합니다.",
    },
    cta: {
      overline: "인스피레이션이 되세요",
      heading: "당신의 이야기를.\n당신의 스포트라이트로.",
      primary: "인스피레이션이 되세요 →",
      secondary: "이미 디자이너이신가요? Studio 열기 →",
    },
  },

  // ─── For Brands page ─────────────────────────────────────────────────────
  forBrands: {
    meta: {
      title: "브랜드 · Caratzon",
      description:
        "독립 크리에이터의 오리지널 주얼리 디자인을 소싱하세요. 모든 라이선스에 Essentials Kit(카탈로그 촬영·스펙·SEO 카피) 기본 포함. Caratzon Kit으로 업그레이드하면 AI 생성 라이프스타일 촬영·라인시트·SNS 에셋도 활용 가능합니다. 라이선스 취득 당일 시장 투입.",
    },
    howItWorks: {
      meta: {
        title: "이용 방법 — 브랜드를 위한 Caratzon",
        description: "디스커버리부터 배송까지 — 디자이너 오리지널을 브라우징하고 라이선스 티어를 선택하세요. 제조 네트워크가 생산을 관리하는 동안 마케팅 에셋은 즉시 라이브됩니다.",
      },
      headline: "디스커버리에서\n배송까지.",
      intro: "인증된 디자이너 오리지널을 브라우징합니다. 라이선스를 선택합니다. 제조 네트워크가 생산을 관리하는 동안 마케팅 에셋은 즉시 라이브됩니다. 정확한 프로세스를 안내해 드리겠습니다.",
    },
    hero: {
      overline: "주얼리 브랜드 · 부티크 · 유통사를 위해",
      h1Line1: "다음 컬렉션.",
      h1Line2: "R&D 없이. 에이전시 없이.",
      h1Line3: "관리할 공장 없이.",
      tagline:
        "독립 크리에이터의 오리지널 디자인을 브라우징합니다. 마음에 드는 디자인을 라이선스 — 카탈로그 촬영, 스펙, 마케팅 에셋이 모든 피스에 포함됩니다. 생산은 EPH 네트워크를 통해 진행됩니다. 소싱부터 진열까지, 하나의 플랫폼.",
      ctaPrimary: "트레이드 카탈로그 보기",
      ctaSecondary: "이용 방법 보기 →",
    },
    marquee: {
      ariaLabel: "카탈로그 미리보기",
    },
    badges: {
      fullIp: "풀 IP",
    },
    stats: {
      tiers: "취득 티어",
      designerDirect: "디자이너 다이렉트",
      noIntermediaries: "중간 유통 없음",
      contentReady: "마케팅 포함",
      contentReadySub: "모든 라이선스에 Essentials Kit 포함 · Caratzon Kit 업그레이드 가능",
      productionManaged: "생산 관리",
      endToEnd: "엔드투엔드",
    },
    acquire: {
      overline: "취득 모델",
      subtitle: "당신의 컬렉션, 당신의 조건으로",
      heading: "Caratzon 디자인을\n취득하는 3가지 방법.",
      models: [
        {
          tier: "Standard",
          label: "활성화 & 에셋 수수료",
          heading: "스탠다드 라이선스",
          description:
            "단순 허가증이 아닙니다 — 수수료의 몇 배에 달하는 가치를 지닌 숏컷입니다. 생산 준비 완료 디자인과 Essentials Kit(카탈로그 촬영·SEO 상품 카피·정밀 기술 스펙)을 취득합니다. 계약 당일 스토어에 게시 가능합니다. Caratzon Kit으로 업그레이드하면 라이프스타일 촬영·라인시트·SNS 에셋도 활용 가능합니다.",
          gets: [
            "Essentials Kit 포함 — 카탈로그 촬영·SEO 상품 스토리·스펙 즉일 제공",
            "Caratzon Kit 업그레이드 가능 — 라이프스타일 촬영·라인시트·SNS 에셋",
            "즉시 제조 접근 — 생산 파이프라인에서 즉각 언락",
            "R&D 비용 제로 — 양산용으로 검증된 프리엔지니어드 CAD",
            "상업적 권리 — 자사 브랜드로 화이트 라벨 판매, 무기한",
          ],
          benefit: "Essentials Kit 포함 · R&D 생략 · 당일 런칭",
        },
        {
          tier: "Embargo",
          label: "12개월 독점 제조 권리",
          heading: "시즈널 엠바고",
          description:
            "다음 캠페인을 위해 디자인을 잠급니다. 12개월간 독점 제조 권리 — 엠바고 기간 동안 마켓플레이스에서 비공개 처리됩니다. 경쟁사의 생산이 불가능합니다. 엠바고 해제 시 디자인이 마켓으로 복귀합니다.",
          gets: [
            "독점 잠금 — 기간 중 경쟁사 생산 제로",
            "스탠다드 라이선스의 모든 혜택 포함",
            "Essentials Kit 포함 — 12개월간 독점 카탈로그 촬영 & 스토리. Caratzon Kit 업그레이드 가능",
            "엠바고 전 기간 동안 마켓플레이스에서 비공개",
          ],
          benefit: "경쟁 차단 윈도우 · 독점 콘텐츠 · 히어로 프로덕트 가능성",
        },
        {
          tier: "Vault",
          label: "풀 독점권 (IP 양도)",
          heading: "풀 독점권",
          description:
            "IP를 영구 소유합니다. 영구적 소유권을 취득 — 디자인이 마켓플레이스에서 즉시 삭제됩니다. 결제 완료 시 생산 준비 완료 원본 파일(.STL / .3DM)을 직접 수령합니다. 다른 브랜드는 이 디자인을 소싱할 수 없습니다. 소스 파일이 양도되는 유일한 시나리오입니다.",
          gets: [
            "영구 IP 소유권 — 디자인이 마켓플레이스에서 은퇴",
            "원본 생산 파일(.STL / .3DM) 직접 양도",
            "Caratzon Kit 포함 — 에이전시 불필요. AI로 브랜드 전용 라이프스타일 촬영·라인시트·소셜 키트를 무제한 생성",
            "디자이너 설정 최저 가격 — 그 위에서 협상",
          ],
          benefit: "영구 독점 · Caratzon Kit 포함 · CAD 직접 보유",
        },
      ],
      whyStandard: {
        label: "스탠다드 라이선스 수수료가 왜 필요한가요?",
        heading: "허가증 비용이 아닙니다. 압도적인 숏컷에 대한 투자입니다.",
        points: [
          {
            heading: "NRE(초기 개발비) 생략",
            body: "기존 제조에서는 1 디자인당 3D 모델링·왁스 프린트·테스트 캐스팅·수정으로 $500–$2,000 이상이 발생합니다. 스탠다드 라이선스는 이 모든 것을 대체합니다 — 파일은 이미 설계·스트레스 테스트 완료됨.",
          },
          {
            heading: "Essentials Kit — 오늘 스토어에 게시",
            body: "모든 스탠다드 라이선스에 Essentials Kit 포함: 고해상도 카탈로그 촬영·SEO 최적화 상품 설명·정밀 기술 스펙 — 라이선스 취득 순간 생성됩니다. 계약 당일 스토어에 게시 가능합니다. 풀 크리에이티브 스위트가 필요해지면 Caratzon Kit으로 업그레이드하여 AI 생성 라이프스타일 촬영·브랜드 라인시트·게시 준비 SNS 에셋을 이용할 수 있습니다.",
          },
          {
            heading: "화이트 라벨, 법적 리스크 제로",
            body: "자사 브랜드명·자사 패키징으로 판매하는 완전한 상업적 권리를 취득합니다. 디자이너는 저작권 불행사에 동의합니다. 리스크 없이 비즈니스를 구축하세요.",
          },
        ],
      },
      ipNotes: {
        standardLabel: "스탠다드 라이선스 & 시즈널 엠바고",
        standardBody:
          "생산 파일은 Caratzon이 엄중 관리하며, NDA 하의 검증된 제조 파트너에게만 제공됩니다. 원본 CAD는 수령 불가 — 생산은 당사 네트워크를 통해 자동 라우팅됩니다.",
        vaultLabel: "풀 독점권",
        vaultBody:
          "결제 완료 시 디자이너의 생산 준비 완료 .STL / .3DM 파일이 직접 양도됩니다. 디자인은 카탈로그에서 영구 은퇴 — 다른 브랜드가 소싱할 수 없습니다.",
      },
    },
    process: {
      overline: "이용 방법",
      subtitle: "디스커버리에서 딜리버리까지",
      items: [
        {
          heading: "발견",
          body: "인증된 디자이너 오리지널을 브라우징합니다. 각 피스의 Essentials Kit를 미리 확인하세요 — 카탈로그 촬영·스펙·에디토리얼 스토리·인터랙티브 3D 뷰어 — 커밋 전에 전체 패키지를 평가할 수 있습니다.",
        },
        {
          heading: "라이선스",
          body: "티어를 선택합니다. 소싱 요청을 제출하고 디자이너와 직접 조건을 협상한 후 라이선스를 확정합니다. 모두 문서화되어 법적으로 구조화됩니다.",
        },
        {
          heading: "생산",
          body: "주문이 제조 네트워크로 자동 라우팅됩니다 — 캐스팅·세팅·QC·물류를 엔드투엔드로 관리. 생산 진행 중에도 Essentials Kit은 이미 스토어에 공개. Caratzon Kit으로 업그레이드하면 라이프스타일 촬영·라인시트·SNS 에셋도 동시 공개. 재고 도착 전에 프로모션을 시작할 수 있습니다.",
        },
      ],
      quote: "\"오리지널 소싱. 대량 제조.\n공장 관계 불필요.\"",
      bullets: [
        "아시아·유럽의 검증된 제조 네트워크",
        "캐스팅·세팅·QC·물류를 엔드투엔드로 관리",
        "모든 라이선스에 Essentials Kit 기본 포함 — 카탈로그 촬영·스펙·SEO 카피",
        "Caratzon Kit 업그레이드: AI 라이프스타일 촬영·브랜드 라인시트·SNS 에셋 온디맨드",
      ],
      teaserItems: [
        { heading: "발견", body: "풀 미리보기와 함께 디자이너 오리지널을 브라우징." },
        { heading: "라이선스", body: "티어를 선택하고 상업적 권리를 확정." },
        { heading: "생산", body: "에셋이 라이브되는 동안 제조를 엔드투엔드로 관리." },
      ],
      teaserCta: "이용 방법 보기 →",
    },
    vs: {
      overline: "기존 도매와의 비교",
      traditional: "기존 방식",
      caratzon: "Caratzon",
      rows: [
        ["출처", "불분명한 원산지, 다단계 유통망", "인증된 독립 디자이너로부터 다이렉트"],
        ["R&D / NRE 비용", "디자인당 $500–$2,000+(3D 모델링·왁스 프린트·테스트 캐스팅·수정)", "제로 — 양산용으로 프리엔지니어드 & 검증"],
        ["시장 투입 시간", "게시까지 수 주간의 엔지니어링+샘플링", "오늘 라이선스, 오늘 스토어 게시 — Essentials Kit 즉시 포함"],
        ["마케팅 에셋", "포토그래퍼·스타일리스트·카피라이터 섭외 — 수 주, 컬렉션당 수천 달러", "Essentials Kit 즉시 포함. AI 생성 Caratzon Kit(라이프스타일·소셜·라인시트) 온디맨드 제공"],
        ["상업적 권리", "별도 협상, 불명확한 경우 다수", "화이트 라벨 권리 포함 — 자사 브랜드로 무기한 판매"],
        ["독점성", "경쟁사를 차단할 메커니즘 없음", "스탠다드, 시즈널 엠바고(12개월), 풀 독점권(영구)"],
        ["생산", "자체 공장 관계 관리", "제조 네트워크를 통해 자동 라우팅"],
      ],
    },
    studio: {
      overline: "Caratzon Studio",
      heading: "디자인을 소싱하세요.\n마케팅은 AI가 생성합니다.",
      intro:
        "새로운 재고 소싱은 촬영을 위해 샘플 도착을 수 주간 기다리는 것을 의미했습니다. Caratzon Studio와 함께라면 라이선스 취득 순간 마케팅 에셋이 생성됩니다. 런칭 전략을 선택하세요:",
      essentials: {
        heading: "Essentials Kit",
        badge: "모든 라이선스에 포함",
        body: "데이터 입력이 필요 없습니다. 디자인을 라이선스하는 순간 생산 준비 스펙·선명한 카탈로그 촬영·SEO 카피가 즉시 생성됩니다. 오늘 스토어에 신규 재고를 게시하기 위한 완벽한 기반입니다.",
        features: [
          "고해상도 화이트 배경 이커머스 촬영",
          "SEO 최적화 상품 설명",
          "정밀 기술·소재 스펙",
        ],
      },
      caratzonKit: {
        heading: "Caratzon Kit",
        badge: "프리미엄 런칭 업그레이드",
        body: "포토 슈트 불필요. 원클릭으로 크리에이티브 에이전시를 대체합니다. 로고·브랜드 컬러·미학을 라이프스타일 촬영·라인시트·SNS 키트에 매끄럽게 통합합니다.",
        features: [
          "AI 생성 라이프스타일 & 온모델 포토그래피",
          "브랜드 스타일에 맞춘 자동 포맷 PDF 도매 라인시트",
          "게시 준비 SNS 에셋(Reels/TikTok 크롭 & 캡션)",
        ],
      },
    },
    caraAI: {
      overline: "Cara AI · 엔지니어링 인텔리전스",
      heading: "제너레이티브 이미지 도구가 아닙니다.\n주얼리 엔지니어링 AI입니다.",
      intro: "대부분의 AI는 예쁜 이미지만 생성합니다. Cara는 공장 현장에서 살아남고 손에 착용했을 때 편안한 디자인을 생성합니다. 결과물은 무드보드가 아닌 — 완전한 부품표가 첨부된 생산 가능한 블루프린트입니다.",
      pillars: [
        {
          stat: "±0.15mm",
          label: "수축 보정",
          body: "Cara는 로스트 왁스 캐스팅 시 금속별 수축률을 고려합니다 — 골드, 실버, 플래티넘, 브라스 — 최종 제품이 디지털 모델과 일치하도록 설계합니다.",
        },
        {
          stat: "0.6mm",
          label: "최소 벽두께",
          body: "생성되는 모든 형태는 합금별 최소 벽두께를 준수합니다 — 플래티넘 0.6mm, 골드와 실버 0.8mm. 종이처럼 얇은 프롱도, 무너지는 베젤도 없습니다. 캐스터에게 도달하기 전에 구조적으로 건전합니다.",
        },
        {
          stat: "4-Prong+",
          label: "스톤 공차 매핑",
          body: "Cara는 모든 스톤 유형과 컷에 대해 프롱 배치·시트 깊이·거들 클리어런스를 계산합니다. 일상 착용 시 확실한 고정을 위해 설계됩니다 — 시각적 타당성만이 아닙니다.",
        },
        {
          stat: "R≥0.4mm",
          label: "착용감 · 손 느낌",
          body: "Cara는 밴드 프로파일·내부 곡률·무게 분포·에지 반경을 평가하여 모든 피스가 외관만큼이나 편안함을 보장합니다. 날카로운 에지·톱 헤비 밸런스·핀치 포인트는 디자인이 화면을 떠나기 전에 플래그됩니다.",
        },
      ],
      capabilities: [
        { label: "캐스팅 메커닉스", detail: "결함 없는 캐스팅을 위한 스프루 배치·게이트 사이징·플로우 분석" },
        { label: "구조 해석", detail: "형태 전체에 걸친 스트레스 테스트 벽두께 및 하중 분포 점검" },
        { label: "부품표", detail: "콘셉트 단계에서 정확한 금속 중량·스톤 수량·원가 추정 제공" },
        { label: "합금 인텔리전스", detail: "10K–24K 골드, 925 실버, 950 플래티넘, 브라스의 소재 특성" },
        { label: "프로덕션 레디", detail: "모든 출력을 실제 EPH 제조 공차에 대해 평가" },
        { label: "스케치 to CAD 브릿지", detail: "냅킨 스케치 업로드 — Cara가 지오메트리를 추출하고 생산용으로 설계" },
      ],
      closing: "Cara가 엔지니어링한 콘셉트는 첫 캐스팅에서 성공하도록 설계됩니다 — 재작업 없이, 왁스 낭비 없이, 공장 반려 없이. 바이어가 Cara 콘셉트를 볼 때, 그것은 제조업체가 적용할 동일한 제약 조건으로 이미 스트레스 테스트를 거친 작품입니다. 이것이 제너레이티브 AI와 엔지니어링 AI의 차이입니다.",
      tryCara: "Cara AI 체험하기 — 라이브 데모",
      learnMore: "자세히 보기",
    },
    cta: {
      overline: "소싱 시작",
      heading: "트레이드 카탈로그를\n브라우징하세요.",
      body: "모든 스탠다드 라이선스에 Essentials Kit 기본 포함 — 카탈로그 촬영·스펙·SEO 카피 즉시 제공. Caratzon Kit으로 업그레이드하면 라이프스타일 촬영·라인시트·SNS 에셋도 이용 가능. 3D로 평가하고 기존 파이프라인보다 압도적으로 빠르게 시장에 투입하세요.",
      primary: "디자인 보기",
      secondary: "이용 방법 보기",
      tradeOnly: "트레이드 계정 전용",
    },
  },

  // ─── For Manufacturers page ──────────────────────────────────────────────
  forManufacturers: {
    meta: {
      title: "프로덕션 · Caratzon",
      description:
        "모든 Caratzon 주문은 EPH에서 생산됩니다 — 중국 최대 통합형 주얼리 제조 허브. 5,000+ 제조사, 친환경 인증 도금, CAD에서 배송까지 완전 추적성.",
    },
    hero: {
      overline: "프로덕션 · EPH Manufacturing Hub",
      h1Line1: "하나의 시설.",
      h1Line2: "모든 역량.",
      h1Line3: "5,000+ 제조사.",
      tagline:
        "모든 Caratzon 주문은 EPH를 통해 라우팅됩니다 — 중국 최대 통합형 주얼리 제조 허브. 한 지붕 아래 5,000+ 제조사, 인증 친환경 도금, CAD 파일에서 완성품까지 완전 추적성.",
      cta: "카탈로그 보기",
    },
    stats: {
      cadReady: "5,000+",
      everyOrder: "한 지붕 아래의 제조사",
      phases: "풀 체인",
      milestone: "캐스팅 · 세팅 · 폴리싱 · 도금 · QC",
      ecoPlating: "친환경 인증",
      ephCertified: "수처리 시설 완비",
      fullTrace: "풀 트레이스",
      clientVis: "왁스 프린트에서 배송까지",
    },
    orderContents: {
      overline: "제조 프로세스",
      subtitle: "파일에서 완성품까지",
      items: [
        {
          heading: "CAD에서 왁스 프린트",
          body: "생산 파일이 EPH로 라우팅되어 물리적 왁스 마스터로 변환됩니다 — 디지털 디자인에서 유형 피스로의 첫 단계입니다.",
        },
        {
          heading: "캐스팅 & 세팅",
          body: "로스트 왁스 캐스팅, 스톤 세팅, 조립이 동일 시설에서 이루어집니다. 공장 간 핸드오프 없음, 공차 손실 없음.",
        },
        {
          heading: "친환경 도금 & 피니싱",
          body: "인증 친환경 라인에서의 골드·로듐·합금 도금, 현장 수처리 시설 완비. 스펙에 맞는 폴리싱 & 피니싱.",
        },
        {
          heading: "QC & 배송",
          body: "출하 전 다중 포인트 품질 관리. 대시보드에서 생산 마일스톤 실시간 확인, 완료 시 운송장 발행.",
        },
      ],
    },
    workflow: {
      overline: "프로덕션 단계",
      subtitle: "주문 단계별 안내",
      phases: [
        ["주문 확인", "디자인이 EPH 생산 대기열에 진입"],
        ["NRE & 테스트 캐스트", "왁스 프린트 제작, 승인용 샘플 제출"],
        ["캐스팅", "금속 주입, 중량 및 합금 검증"],
        ["세팅 & 조립", "스톤 세팅, 공차 점검, 부품 조립"],
        ["도금 & 피니싱", "스펙에 따른 친환경 인증 도금 적용"],
        ["QC & 출하", "최종 다중 포인트 검사, 운송장 발행"],
      ],
    },
    eph: {
      overline: "EPH Manufacturing Hub",
      location: "중국",
      headline: "5,000+ 제조사. 한 지붕. 풀 공급망.",
      body: "EPH는 중국 최대 통합형 주얼리 제조 허브이자 모든 Caratzon 주문의 전용 생산 시설입니다. 인증 수처리 시설, 친환경 인증 도금 라인, 한 지붕 아래의 풀 프로덕션 체인으로 대규모 지속가능 파인 주얼리 제조의 벤치마크를 확립합니다.",
      attrs: [
        {
          label: "통합형 공급망",
          body: "캐스팅·세팅·폴리싱·도금을 단일 시설에서. 공장 간 물류 없음, 핸드오프 손실 없음.",
        },
        {
          label: "인증 친환경 도금",
          body: "현장 수처리 시설을 갖춘 친환경 인증 라인에서의 골드·로듐·합금 피니시 — 화학 배출 저감 검증.",
        },
        {
          label: "규모 & 전문화",
          body: "브라이들부터 오트 쿠튀르까지 모든 주얼리 카테고리에서 깊은 전문성을 보유한 5,000+ 제조사.",
        },
        {
          label: "다이렉트 디지털 통합",
          body: "CAD 파일이 Caratzon 플랫폼에서 EPH의 생산 대기열로 직접 라우팅 — 수동 재입력 제로, 완전 추적성.",
        },
      ],
    },
    cta: {
      overline: "카탈로그 보기",
      heading: "생산 가능한 디자인을\n확인하세요.",
      primary: "카탈로그 보기",
      secondary: "제조 파트너로 지원 →",
    },
    inquiryForm: {
      overline: "파트너 신청",
      heading: "제조 네트워크에\n참여를 신청하세요.",
      subtitle:
        "시설과 역량에 대해 알려주세요. 파트너십 팀이 신청을 검토하고 3영업일 이내에 연락드립니다.",
      companyName: "회사명 / 공장명",
      contactName: "담당자명",
      email: "비즈니스 이메일",
      phone: "전화번호",
      website: "회사 웹사이트",
      location: "공장 소재지(도시, 국가)",
      capabilities: "제조 역량",
      capabilitiesHint: "해당하는 항목을 모두 선택하세요",
      capabilityOptions: [
        "Casting",
        "Stone Setting",
        "Plating",
        "Polishing",
        "CAD/CAM",
        "CNC",
        "Assembly",
        "Engraving",
        "Enameling",
        "QC / Inspection",
      ],
      specializations: "주얼리 전문 분야",
      specializationsHint: "해당하는 항목을 모두 선택하세요",
      specializationOptions: [
        "Rings",
        "Earrings",
        "Necklaces",
        "Bracelets",
        "Bridal",
        "Men's",
        "Watches",
        "Bespoke",
      ],
      certifications: "인증",
      certificationsHint: "해당하는 항목을 모두 선택하세요",
      certificationOptions: [
        "RJC",
        "ISO 9001",
        "ISO 14001",
        "Hallmark",
        "Fairmined",
        "BSCI / Sedex",
      ],
      productionCapacity: "월간 생산 용량(피스)",
      capacityOptions: [
        "500 미만",
        "500 – 2,000",
        "2,000 – 10,000",
        "10,000 – 50,000",
        "50,000+",
      ],
      additionalInfo: "추가 정보",
      additionalInfoPlaceholder:
        "기타 알려주실 사항 — 전문 분야, 리드 타임, 기존 파트너십 등.",
      submit: "신청서 제출",
      submitting: "제출 중…",
      successHeading: "신청이 접수되었습니다",
      successBody:
        "Caratzon 제조 네트워크에 관심을 가져주셔서 감사합니다. 파트너십 팀이 신청을 검토하고 3영업일 이내에 연락드리겠습니다.",
      errorRequired: "필수 항목을 모두 입력해 주세요.",
      errorEmail: "유효한 이메일 주소를 입력해 주세요.",
      errorSend: "오류가 발생했습니다. 다시 시도하시거나 직접 이메일로 문의해 주세요.",
    },
  },

  // ─── Partner Application page ────────────────────────────────────────────
  partnerApplication: {
    meta: {
      title: "파트너 신청 · Caratzon",
      description:
        "Caratzon 제조 네트워크 참여를 신청하세요. 시설, 역량, 인증에 대해 알려주세요.",
    },
  },

  // ─── Contact page ──────────────────────────────────────────────────────
  contact: {
    meta: {
      title: "문의 · Caratzon",
      description:
        "Caratzon 팀에 문의하세요. 트레이드 데스크, 디자이너 연결, 일반 문의.",
    },
    overline: "문의",
    heading: "Caratzon에 문의하기",
    subtitle:
      "독점 디자인을 소싱하려는 리테일 브랜드이든, IP를 수익화하려는 3D CAD 엔지니어이든, 글로벌 팀이 도와드리겠습니다.",
    tabs: {
      brand: "브랜드 / 리테일러",
      designer: "디자이너",
      general: "일반 / 보도 / 투자자",
    },
    brand: {
      headline: "트레이드 데스크에 상담하기",
      copy: "R&D를 생략하고 제조를 효율화하고 싶으신가요? 플랫폼 접근, 대량 소싱, EPH 제조 역량에 대해 소싱 전문가와 상담하세요.",
      firstName: "이름",
      lastName: "성",
      workEmail: "업무용 이메일",
      companyName: "회사명 / 브랜드명",
      companyWebsite: "회사 웹사이트",
      helpWith: "문의 내용",
      helpOptions: [
        "Source / Atelier 플랜으로 업그레이드",
        "대량 소싱",
        "EPH 제조 역량",
        "Caratzon Studio AI 데모",
        "플랫폼 일반 질문",
      ] as readonly string[],
      selectPlaceholder: "토픽을 선택하세요…",
      message: "메시지",
      messagePlaceholder: "소싱 니즈, 타임라인, 구체적 질문을 알려주세요…",
      submit: "메시지 보내기",
      submitting: "전송 중…",
    },
    designer: {
      headline: "Caratzon 디자이너 네트워크에 참여",
      copy: "글로벌 라이선싱 마켓플레이스에서 3D CAD 주얼리 디자인을 수익화하세요. 포트폴리오를 업로드하고 조건을 설정하면 브랜드가 찾아옵니다.",
      firstName: "이름",
      lastName: "성",
      email: "이메일",
      portfolioUrl: "포트폴리오 / Behance / Instagram URL",
      cadSoftware: "주요 CAD 소프트웨어",
      cadOptions: [
        "Rhino / Grasshopper",
        "Matrix / MatrixGold",
        "ZBrush",
        "Blender",
        "SolidWorks",
        "기타",
      ] as readonly string[],
      selectPlaceholder: "주요 도구를 선택하세요…",
      designCount: "디자인 개수(대략)",
      designCountOptions: [
        "1–10",
        "11–50",
        "51–200",
        "200+",
      ] as readonly string[],
      designCountPlaceholder: "범위를 선택하세요…",
      message: "작품에 대해 알려주세요",
      messagePlaceholder: "디자인 스타일, 전문 분야, 라이선싱 파트너십에서 원하는 것을 설명해 주세요…",
      submit: "디자이너로 신청",
      submitting: "전송 중…",
    },
    general: {
      headline: "일반 문의",
      copy: "보도, 파트너십, 투자자 관련 문의는 기업 팀에 연락해 주세요.",
      name: "이름",
      email: "이메일",
      topic: "토픽",
      topicOptions: [
        "보도 · 미디어",
        "IR 정보",
        "파트너십",
        "기타",
      ] as readonly string[],
      selectPlaceholder: "토픽을 선택하세요…",
      message: "메시지",
      messagePlaceholder: "어떻게 도와드릴까요?",
      submit: "문의 보내기",
      submitting: "전송 중…",
    },
    success: {
      heading: "메시지가 접수되었습니다",
      body: "문의해 주셔서 감사합니다. 24시간 이내에 팀원이 답변드리겠습니다. 그동안 플랫폼을 탐색해 보세요.",
      backToHome: "홈으로 돌아가기",
      explorePlatform: "플랫폼 탐색 →",
    },
    errors: {
      required: "필수 항목을 모두 입력해 주세요.",
      invalidEmail: "유효한 이메일 주소를 입력해 주세요.",
      sendFailed: "오류가 발생했습니다. 다시 시도하시거나 hello@caratzon.com으로 직접 이메일을 보내주세요.",
    },
    offices: {
      heading: "글로벌 오피스",
      hongkong: { city: "홍콩", label: "아시아 태평양 트레이드 데스크" },
      guangdong: { city: "광둥", label: "제조 & 운영 본부" },
      london: { city: "런던", label: "유럽 트레이드 데스크" },
    },
  },

  // ─── About page ──────────────────────────────────────────────────────────
  about: {
    meta: {
      title: "Caratzon 소개 — 우리의 이야기",
      description:
        "Caratzon은 독립 주얼리 디자이너와 월드클래스 제조를 연결하는 글로벌 양방향 에코시스템입니다. 주얼리 공급망 혁신에 대해 알아보세요.",
    },
    hero: {
      overline: "Caratzon 소개 · Est. 2024",
      h1Line1: "탁월한 디자인과",
      h1Line2: "완벽한 실행이",
      h1Line3: "만나는 곳.",
      tagline:
        "Caratzon은 한 주얼리 브랜드의 홀리데이 시즌 전체를 위험에 빠뜨렸던 단 한 통의 분실된 이메일 스레드에서 탄생했습니다. 오늘날 우리는 탁월한 독립 디자이너와 월드클래스의 풀 매니지드 제조를 연결하는 글로벌 양방향 에코시스템입니다.",
      lastUpdated: "최종 업데이트 2026년 2월",
    },
    stats: {
      overline: "플랫폼 규모",
      subtitle: "숫자로 보는 Caratzon",
      items: [
        { value: "10,000+", label: "독점 CAD 디자인" },
        { value: "150+", label: "검증된 제조 파트너" },
        { value: "1M+", label: "오케스트레이션된 생산 노드" },
        { value: "$50M+", label: "크리에이터에게 환원" },
        { value: "0", label: "블랙박스 공급망 지연" },
      ],
    },
    origin: {
      overline: "우리의 원점",
      subtitle: "모든 플랫폼에는 기원이 있습니다",
      p1: "매일 Caratzon은 크리에이터가 자신의 예술을 수익화하고, B2B 브랜드가 투명한 노드 기반 공급망의 확실한 보장 아래 새롭고 독점적인 컬렉션을 소싱할 수 있도록 합니다.",
      p2: "우리는 글로벌 소싱의 리스크와 마찰을 제거하기 위해 존재합니다. 브랜드가 플랫폼의 디자인에 매료되었을 때, 제조 방법을 고민할 필요가 없습니다. Caratzon이 CAD 엔지니어링, 물리적 프로토타이핑, 멀티 벤더 물류, 품질 관리, 최종 배송까지 엔드투엔드로 처리합니다.",
      digitalDesign: "디지털 디자인",
      precisionCraft: "정밀 크래프트",
    },
    timeline: {
      overline: "Caratzon 스토리",
      subtitle: "4개 페이즈",
      phases: [
        {
          heading: "끊어진 체인",
          subheading: "이야기의 시작",
          label: "과제",
          body: "커스텀 컷 사파이어, 정밀 로스트 왁스 캐스팅, 스페셜 로듐 피니시를 요하는 멋지게 디자인된 멀티 컴포넌트 피스에서 시작되었습니다. 2개 대륙에 걸친 3개 공장으로 CAD 파일을 라우팅한 결과, 공장이 숨기려 했던 작은 기공(porosity) 문제로 인한 대규모 지연이 발생했습니다. PDF 인보이스, WhatsApp 업데이트, 맹목적 신뢰에 의존하는 주얼리 산업은 지속 불가능하다는 것이 명백해졌습니다.",
        },
        {
          heading: "갇혀 있는 재능",
          subheading: "눈앞에 있었던 시장의 단절",
          label: "인사이트",
          body: "동시에 시장에서의 거대한 단절을 인식했습니다. 뛰어난 독립 주얼리 디자이너들은 숨 막히는 3D CAD로 하드 드라이브를 채웠지만, 공장 커넥션도 생산 자본도 전무했습니다. 한편, 대형 B2B 리테일 브랜드는 새로운 미학을 갈망하면서도 미지의 크리에이터를 시도하는 제조 리스크를 두려워했습니다. 예술과 물류는 근본적으로 양립할 수 없었습니다.",
        },
        {
          heading: "운영 체제 구축",
          subheading: "중추 신경계의 엔지니어링",
          label: "구축",
          body: "궁극의 다리를 건설하기로 했습니다. 주얼리 제조의 중추 신경계로 기능하는 독자적인 모듈러 노드 기반 플랫폼을 엔지니어링했습니다. 테크니컬 디자이너의 비전을 외부 공장에 직접 연결하고, 실시간 P&L 마진을 추적하며, 모든 기술 스펙을 보호하면서 — 개별 벤더는 완전히 격리된 상태로 유지합니다.",
        },
        {
          heading: "궁극의 마켓플레이스",
          subheading: "디자인이 완벽한 실행을 만나다",
          label: "결과",
          body: "Caratzon은 단순 추적 도구가 아닌, 풀 매니지드 제조 엔진이 뒷받침하는 큐레이티드 디자이너 마켓플레이스로 정식 런칭했습니다. 오늘날 브랜드가 플랫폼의 디자인에 매료되어도 제조 방법을 고민할 필요가 없습니다. 원클릭으로 Caratzon이 검증된 네트워크를 통해 자동 라우팅하여 CAD 엔지니어링, 물리적 프로토타이핑, 최종 품질 관리를 처리합니다.",
        },
      ],
    },
    vision: {
      overline: "비전 & 미션",
      visionLabel: "비전",
      visionBody:
        "월드클래스 주얼리 디자인이 완벽한 실행과 매끄럽게 만나는 결정적 글로벌 에코시스템이 되는 것 — 파편화된 공급망을 크리에이터와 브랜드 양쪽 모두에게 투명하고 고수익적인 엔진으로 전환하는 것.",
      missionLabel: "미션",
      missionBody:
        "독립 주얼리 디자이너를 풀 매니지드 제조 운영 체제로 뒷받침되는 글로벌 B2B 브랜드와의 연결을 통해 강화하는 것. 글로벌 소싱의 리스크와 마찰을 제거하기 위해 우리는 존재합니다.",
    },
    howItWorks: {
      overline: "이용 방법",
      subtitle: "원클릭. 엔드투엔드.",
      steps: [
        {
          heading: "디자이너가 업로드",
          body: "독립 디자이너가 CAD 파일을 업로드하고, IP 조건을 설정하고, 에디토리얼 스토리를 공개합니다. 작품은 서명된 만료 기한 뷰어 URL로 보호됩니다.",
        },
        {
          heading: "브랜드가 발견",
          body: "리테일 브랜드가 큐레이티드 카탈로그에서 10,000+ 독점 디자인을 브라우징하고, 플랫폼 내 인터랙티브 3D 뷰어로 피스를 미리 확인합니다.",
        },
        {
          heading: "Caratzon이 라우팅",
          body: "합의 후, 노드 기반 엔진이 디자인을 검증된 제조 네트워크를 통해 자동 라우팅합니다 — CAD 엔지니어링, 프로토타이핑, QC.",
        },
        {
          heading: "마진 확보 배송",
          body: "실시간 P&L 가시성, 마일스톤 추적, 최종 배송 — 블랙박스 지연 제로, 공급망 전체의 완전한 투명성.",
        },
      ],
    },
    leadership: {
      overline: "경영진",
      founderRole: "창업자 겸 CEO",
      founderName: "[창업자명]",
      founderBio:
        "주얼리 디자인, 공급망 관리, 기술 분야의 배경과 Caratzon 비전을 이끄는 원동력에 대한 소개.",
      tags: ["Jewelry Design", "Supply Chain", "Technology"],
    },
    supplyChain: {
      overline: "공급망 아키텍처",
      heading: "노드 기반.\n완전 투명.",
      items: [
        {
          title: "벤더 완전 격리",
          desc: "개별 공장 노드는 독립적으로 운영됩니다. 벤더 간에 타사의 스펙·가격·용량 데이터가 공유되지 않습니다.",
        },
        {
          title: "실시간 P&L",
          desc: "모든 생산 마일스톤에서 마진 가시성 — 디자이너·브랜드·매니저는 자신의 역할에 관련된 데이터만 확인합니다.",
        },
        {
          title: "불변의 감사 추적",
          desc: "모든 CAD 버전, 승인, 생산 이벤트가 타임스탬프와 함께 기록됩니다 — 완전한 관리 이력을 언제든 확인 가능합니다.",
        },
      ],
    },
    quote: {
      text: "\"예술과 물류는 근본적으로 양립할 수 없었습니다.\n우리는 그 다리를 놓았습니다.\"",
      attribution: "Caratzon · 창립 원칙",
    },
    cta: {
      overline: "Caratzon과 파트너 되기",
      heading: "파인 주얼리의\n미래를 함께.",
      primary: "카탈로그 보기",
      secondary1: "브랜드 · 리테일러 →",
      secondary2: "독립 디자이너 →",
    },
  },

  // ─── For Studio (marketing page) ──────────────────────────────────────────
  forStudio: {
    meta: {
      title: "Caratzon Studio · AI와 주얼러의 작업대가 만나는 곳",
      description: "주얼리 크리에이터를 위한 AI 기반 디자인 워크스페이스. Cara를 만나보세요 — 밀리미터, 모스 경도, 인장 강도로 생각하는 디자인 어시스턴트.",
    },
    hero: {
      overline: "Caratzon Studio",
      h1Line1: "당신의 디자인에는",
      h1Line2: "아틀리에가 필요합니다.",
      tagline: "Caratzon Studio는 독립 주얼리 디자이너가 CAD를 업로드하고, 스토리를 전하고, 나머지는 플랫폼에 맡기는 AI 기반 워크스페이스입니다 — 마케팅, 라이선싱, 생산 라우팅, 그리고 이제 지능형 디자인 어시스턴스까지.",
      cta: "디자이너로 신청 →",
      ctaSecondary: "요금제 보기 →",
    },
    stats: {
      aiAssisted: "AI 지원",
      designTools: "디자인 도구",
      stoneTypes: "50+",
      stoneLabel: "스톤 & 합금 데이터베이스",
      phases: "6",
      phasesLabel: "스케치에서 판매까지 단계",
      renders: "즉시",
      rendersLabel: "카탈로그 렌더",
    },
    cara: {
      overline: "Cara를 만나보세요",
      subtitle: "스케치에서 판매까지",
      heading: "냅킨 스케치에서\n글로벌 판매까지.",
      p1: "Cara는 손으로 그린 스케치를 생산 준비 완료, 마켓 등록된 주얼리 피스로 변환하는 AI 디자인 어시스턴트입니다 — 콘셉트 확인부터 카탈로그 렌더, 스토리텔링, 트레이드 등록까지 모든 단계를 처리합니다.",
      p2: "스케치 사진을 찍으세요. Cara가 적절한 질문을 합니다 — 소재, 치수, 스톤 배치 — 그런 다음 모든 곡선과 비율까지 원본 비전을 충실히 재현하는 콘셉트를 렌더링합니다.",
      pipeline: [
        { num: "01", label: "스케치", heading: "스케치 업로드", body: "손으로 그린 스케치나 냅킨 낙서를 촬영하세요. Cara의 비전 모델이 실루엣·비율·스톤 배치·장식 디테일을 분석합니다 — 외과적 정밀도로 의도를 추출합니다.", icon: "pencil" },
        { num: "02", label: "명확화", heading: "지능형 Q&A", body: "단 한 픽셀도 건드리기 전에 Cara가 명확화 질문을 합니다 — 금속 유형, 스톤 컷, 반지 사이즈, 피니시 선호도. 답변이 충실도를 확정하는 디자인 브리프가 됩니다.", icon: "message-circle" },
        { num: "03", label: "렌더", heading: "충실한 AI 렌더", body: "Cara가 스케치를 극도의 충실도로 복제하는 포토리얼리스틱 콘셉트를 생성합니다 — 같은 실루엣, 같은 비율, 같은 스톤 배치. 대화를 통해 완벽해질 때까지 반복합니다.", icon: "image" },
        { num: "04", label: "저장", heading: "Studio에 저장", body: "원클릭으로 승인된 렌더를 Studio에 새 디자인 드래프트로 저장합니다 — 원본 스케치, 대화 기록, 향후 개선을 위한 모든 디자인 매개변수가 포함됩니다.", icon: "save" },
        { num: "05", label: "스토리", heading: "메이킹 내러티브", body: "스케치와 전체 Cara 대화가 AI 생성 '메이킹 오브' 스토리의 원재료가 됩니다 — 첫 선에서 최종 렌더까지의 크리에이티브 여정을 추적하는 매력적인 에디토리얼 내러티브.", icon: "book-open" },
        { num: "06", label: "판매", heading: "등록 & 수익", body: "카탈로그 렌더, 룩북 에셋, 트레이드 등록이 자동 생성됩니다. 디자인이 글로벌 카탈로그에 등록되어 브랜드가 발견·라이선스·주문하고 — 모든 거래에서 80%를 획득합니다.", icon: "trending-up" },
      ],
      conversationDemo: {
        label: "Cara — 스케치에서 콘셉트로",
        userSketch: "이 칵테일 링을 스케치했는데 — 실제 콘셉트로 만들어줄 수 있나요?",
        caraAnalysis: "대담한 비대칭 밴드에 텐션 세팅된 센터 스톤과 테이퍼드 숄더가 보입니다. 렌더 전에 몇 가지 확인하겠습니다:",
        caraQuestions: [
          "어떤 금속을 구상하시나요? 각진 라인은 플래티넘이나 화이트 골드를 시사합니다",
          "센터 스톤 — 에메랄드 컷인가요? 대략 몇 캐럿인가요?",
          "밴드에 텍스처 마크가 보입니다 — 브러시드 피니시인가요, 해머드인가요?",
        ],
        userReply: "플래티넘, 2ct 에메랄드컷 에메랄드, 밴드는 브러시드 피니시.",
        caraConfirm: "완벽합니다. 콘셉트를 렌더링합니다 — 같은 실루엣, 같은 비율, 플래티넘 브러시드 피니시에 2ct 에메랄드컷 에메랄드 텐션 세팅…",
        toolLabel: "충실한 콘셉트 렌더링 중…",
      },
      cta: "Studio에서 Cara 체험하기 →",
    },
    storyLab: {
      overline: "AI Story Lab",
      subtitle: "자동화된 스토리텔링",
      heading: "당신의 이야기를,\n아름답게.",
      p1: "온보딩 과정에서 7가지 안내 질문에 답하면 AI가 풍성한 에디토리얼 스타일의 디자이너 스토리, 프로필 페이지, 룩북을 생성합니다 — 과거에는 카피라이터, 포토그래퍼, 아트 디렉터가 필요했던 수준의 콘텐츠.",
      p2: "각 디자이너는 바이어가 일반적인 상품 리스팅이 아닌 AI 생성 내러티브를 통해 작품을 발견하는 전용 Inspire 프로필을 갖게 됩니다.",
      features: [
        { label: "에디토리얼 프로필", description: "매거진 특집처럼 읽히는 AI 생성 디자이너 스토리 — 템플릿 바이오가 아닙니다." },
        { label: "AI 룩북", description: "디자인에서 자동 생성되는 포트폴리오 전반의 일관된 비주얼 내러티브." },
        { label: "컬렉션 스토리텔링", description: "피스를 테마 컬렉션으로 그룹핑하면 AI가 세트 전체에 걸친 내러티브를 엮어냅니다." },
      ],
    },
    workspace: {
      overline: "디자이너 워크스페이스",
      subtitle: "모든 것을 한 곳에서",
      heading: "업로드. 관리.\n수익.",
      p1: "Studio 워크스페이스는 당신의 컨트롤 센터입니다 — CAD 파일 업로드, 라이선싱 수익 추적, 컬렉션 관리, 분석 모니터링, 바이어와의 커뮤니케이션. 모두 하나의 대시보드에서.",
      features: [
        { num: "01", heading: "3D 뷰어", body: "바이어가 라이선스 전에 풀 3D로 디자인을 검사합니다. CAD 파일은 노출되지 않습니다 — 렌더와 미리보기만 제공." },
        { num: "02", heading: "수익 대시보드", body: "라이선스 수수료, 로열티, 생산 현황을 실시간 추적. 모든 라이선스에 80% 수익 배분." },
        { num: "03", heading: "IP 보호", body: "CAD 파일은 볼트 암호화되어 바이어에게 표시되지 않습니다. 명확한 조건의 구조화된 라이선싱 티어." },
        { num: "04", heading: "다이렉트 메시징", body: "바이어 및 생산 관리자와 플랫폼 내에서 직접 소통합니다." },
      ],
    },
    revenue: {
      overline: "수익 모델",
      subtitle: "3가지 라이선싱 티어",
      heading: "당신의 조건으로\n수익을 얻으세요.",
      models: [
        { tier: "standard", label: "스탠다드 라이선스", heading: "비독점", description: "여러 바이어가 동일 디자인을 라이선스할 수 있습니다. 라이선스 수수료의 80%와 모든 프로덕션 런에 대한 영구 로열티를 보유합니다.", benefit: "80% + 영구 로열티" },
        { tier: "embargo", label: "시즈널 엠바고", heading: "12개월 독점", description: "한 바이어가 1년간 경쟁 차단 독점을 얻습니다. 높은 선불금. 12개월 후 디자인은 스탠다드 가용으로 복귀합니다.", benefit: "80% + 프리미엄 독점 수수료" },
        { tier: "vault", label: "풀 독점", heading: "영구 IP 양도", description: "CAD 파일 포함 영구 IP 양도의 일시불 매입. 최고 선불금. Caratzon Kit 포함.", benefit: "풀 매입 + Caratzon Kit" },
      ],
    },
    steps: {
      overline: "시작하기",
      subtitle: "3단계",
      items: [
        { heading: "신청 & 온보딩", body: "7가지 질문 온보딩 플로우를 완료합니다. AI가 에디토리얼 프로필, 디자이너 스토리, Inspire 페이지를 자동 생성합니다." },
        { heading: "업로드 & 생성", body: "CAD 파일을 업로드합니다. Cara를 사용하여 디자인을 다듬고, 구조 분석을 실행하고, 카탈로그 렌더를 생성합니다. 피스를 컬렉션으로 구성합니다." },
        { heading: "수익 & 성장", body: "바이어가 디자인을 발견·라이선스·주문합니다. 모든 라이선스에서 80%를 획득합니다. 생산은 EPH 인증 제조 허브가 처리합니다." },
      ],
    },
    cta: {
      overline: "Studio에 참여하기",
      heading: "미래를 디자인하세요.\n나머지는 저희가 처리합니다.",
      primary: "디자이너로 신청 →",
      secondary: "카탈로그 탐색 →",
    },
  },

  // ─── Studio ────────────────────────────────────────────────────────────────
  studio: {
    nav: { guide: "가이드", inbox: "수신함", storyLab: "내 디자인", collections: "컬렉션", cara: "Cara", publishing: "퍼블리싱", admin: "Admin →", catalog: "← 카탈로그", analytics: "분석" },
    notifications: { heading: "알림", empty: "아직 알림이 없습니다." },
    cara: {
      title: "Cara",
      subtitle: "AI 디자인 어시스턴트",
      newSession: "새 디자인 세션",
      placeholder: "비전을 설명하거나 인스피레이션 이미지를 업로드하세요…",
      emptyTitle: "Cara를 만나보세요",
      emptyDescription: "당신의 AI 디자인 어시스턴트 — 마스터 주얼러이자 메커니컬 엔지니어. 비전을 설명하거나 인스피레이션 이미지를 업로드하여 시작하세요.",
      analyzing: "인스피레이션 분석 중…",
      rendering: "디자인 렌더 생성 중…",
      structuralAnalysis: "제조 가능성 시뮬레이션 실행 중…",
      calculatingBom: "부품표 계산 중…",
      generatingVideo: "턴테이블 미리보기 생성 중 — 최대 1분 소요될 수 있습니다…",
      processing: "처리 중…",
      phases: { spark: "스파크", architect: "아키텍트", crucible: "크루시블", bench: "벤치" },
      artifacts: {
        title: "아티팩트",
        empty: "Cara가 생성하면 아티팩트가 여기에 표시됩니다.",
        moodBoards: "무드보드",
        renders: "디자인 렌더",
        structuralAnalysis: "구조 분석",
        materialAnalysis: "소재 분석",
        bom: "부품표",
        videos: "동영상",
      },
      sessions: { empty: "세션 없음", emptyHint: "Cara와 첫 디자인 세션을 시작하세요", untitled: "제목 없는 세션" },
      errors: { streamFailed: "오류가 발생했습니다. 다시 시도해 주세요.", rateLimited: "메시지 전송이 너무 빠릅니다. 잠시 기다려 주세요." },
    },
    gettingStarted: {
      overline: "Designer Studio",
      title: "시작하기",
      subtitle: "3가지 핵심 기능의 비주얼 가이드 — 시작 전에 엔드투엔드 흐름을 파악할 수 있습니다.",
      howItWorks: {
        title: "이용 방법",
        steps: [
          { title: "업로드 & 생성", label: "내 디자인", body: "CAD 파일과 보조 에셋을 업로드하면 AI가 퍼블리케이션 준비 완료 카탈로그 스토리를 생성합니다." },
          { title: "요청에 대응", label: "수신함", body: "브랜드의 커스터마이징 요청에 대응합니다. 콜라보레이션, 조건 협상, 수정 CAD 전달을 하나의 스레드에서 관리합니다." },
          { title: "컬렉션 큐레이션", label: "컬렉션", body: "공개된 디자인을 테마별 에디토리얼 컬렉션으로 그룹핑합니다. AI가 개별 스토리를 일관된 내러티브로 통합합니다." },
        ],
      },
      storyLabWorkflow: {
        title: "디자인 워크플로",
        subtitle: "2개 페이즈로 CAD 파일을 공개된 Inspire 페이지로 변환합니다.",
        phase1: {
          title: "페이즈 1 — 디자인 드래프트",
          subtitle: "5단계 위자드가 AI가 처리하기 전에 플랫폼이 필요로 하는 모든 정보를 수집합니다.",
          steps: [
            { title: "아이덴티티", body: "작품에 이름을 지정하고 간결한 설명을 작성합니다." },
            { title: "가격 & 스펙", body: "기본 라이선스 가격, 금속 중량, 스톤 개수를 설정합니다." },
            { title: "IP 보호", body: "스탠다드, 시즈널 엠바고, 풀 독점권 중 선택합니다." },
            { title: "에셋 업로드", body: "프로덕션 CAD, 3D 뷰어 파일, 히어로 렌더를 업로드합니다." },
            { title: "리뷰 & 생성", body: "내용을 확인하고 디자인 드래프트를 생성합니다." },
          ],
        },
        phase2: {
          title: "페이즈 2 — AI 스토리",
          subtitle: "6단계로 드래프트를 AI 생성 내러티브 & 이미지가 포함된 카탈로그 준비 스토리로 변환합니다.",
          steps: [
            { title: "인스피레이션 키워드", body: "레퍼런스 사진 업로드; AI가 무드·소재·테마를 분석합니다." },
            { title: "키워드 선택", body: "작품의 본질을 가장 잘 포착하는 단어를 선택합니다." },
            { title: "스토리 생성", body: "소재와 의도를 기술; AI가 카탈로그 내러티브를 작성합니다." },
            { title: "스토리 다듬기", body: "AI 드래프트 편집 — 이 텍스트가 바이어가 읽는 내용입니다." },
            { title: "쇼케이스 생성", body: "AI가 섹션·헤드라인·이미지 슬롯으로 카탈로그 스프레드를 구성합니다." },
            { title: "승인 신청", body: "편집을 잠그고 리뷰 팀에 제출합니다." },
          ],
        },
      },
      afterSubmission: {
        title: "제출 후 절차",
        body: "관리자가 스토리와 쇼케이스를 리뷰합니다. 승인된 디자인은 트레이드 카탈로그에 공개되고, 반려된 경우 수정 피드백과 함께 반환됩니다.",
        steps: [
          { label: "제출됨", body: "디자인이 리뷰 대기열에 진입합니다." },
          { label: "리뷰 완료", body: "관리자가 스토리 품질과 에셋 완전성을 평가합니다." },
          { label: "공개됨", body: "승인된 디자인이 트레이드 카탈로그에 게시되어 브랜드가 발견 가능합니다." },
        ],
      },
      fileRequirements: {
        title: "파일 요구사항",
        subtitle: "허용 포맷과 사이즈 제한 빠른 참조.",
        columns: { asset: "에셋", formats: "포맷", maxSize: "최대 크기", required: "필수" },
        rows: [
          { asset: "프로덕션 CAD", formats: ".stl, .3dm, .step", maxSize: "50 MB", required: "예" },
          { asset: "3D 뷰어", formats: ".glb", maxSize: "25 MB", required: "예" },
          { asset: "히어로 렌더", formats: ".jpg, .png, .webp", maxSize: "10 MB", required: "예" },
          { asset: "레퍼런스 사진", formats: ".jpg, .png, .webp", maxSize: "각 5 MB", required: "선택" },
        ],
      },
      ipTiers: {
        title: "IP 티어 해설",
        subtitle: "3가지 라이선싱 모델 — 목표에 맞게 선택하세요.",
        tiers: [
          { title: "스탠다드 라이선스", body: "CAD는 Caratzon이 보유하며, NDA 하의 인증 제조사에게만 제공됩니다. 라이선스 수수료의 80%와 영구 로열티를 획득합니다. 여러 브랜드가 동시에 라이선스 가능합니다." },
          { title: "시즈널 엠바고", body: "한 브랜드가 12개월간 독점권을 보유합니다. 기간 중 디자인이 비공개 처리되며, 엠바고 종료 후 마켓플레이스로 복귀합니다. 프리미엄 가격이 적용됩니다." },
          { title: "풀 독점권", body: "브랜드가 IP를 영구적으로 취득합니다. 프로덕션 CAD가 직접 양도됩니다. 일시불 고액 거래 — 디자인이 마켓플레이스에서 영구 삭제됩니다." },
        ],
      },
      cta: { title: "시작할 준비가 되셨나요?", button: "첫 디자인 시작하기" },
    },
    storyLab: {
      overline: "Designer Studio",
      title: "내 디자인",
      subtitle: "디자인을 업로드하고, AI 스토리를 생성하여 Inspire 페이지에 공개합니다.",
      newDraft: "새 디자인 드래프트",
      overlineLabel: "내 디자인",
      cancel: "취소",
      continue: "계속",
      back: "뒤로",
      stepOf: "단계 {current} / {total}",
      keyboardHint: "팁: Alt + 화살표 키로 단계 이동",
      editing: "편집 중…",
      draftInProgress: "드래프트 작성 중",
      emptyTitle: "아직 디자인이 없습니다",
      emptySubtitle: "첫 디자인을 업로드하여 Inspire 포트폴리오 구축을 시작하세요.",
      newDesign: "새 디자인",
      emptyHint: "또는 사이드바에서 기존 디자인을 선택",
      batchUpload: "일괄 업로드",
      batchUploadSubtitle: "최대 20장의 주얼리 사진을 드롭 — AI가 각 피스의 제목·설명·키워드를 생성",
      batchProcessing: "{count}점 처리 중…",
      batchGenerate: "{count} 디자인 생성",
      batchCreated: "{count} 디자인 생성됨",
      batchDuplicates: "{count}건 중복 건너뜀",
      batchPossibleDuplicates: "{count}건 중복 가능성",
      batchFailed: "{count}건 실패",
      batchDuplicateExact: "\"{title}\"(으)로 이미 업로드됨",
      batchDuplicateNear: "\"{title}\"과(와) 유사할 수 있음({confidence}% 일치)",
      batchViewInLab: "내 디자인에서 보기",
      batchDropHere: "최대 20장의 주얼리 사진을 여기에 드롭",
      batchDropHint: "AI가 각 피스의 제목·설명·키워드를 생성합니다",
      celebrationTitle: "디자인 드래프트가 생성되었습니다!",
      celebrationSubtitle: "디자인이 준비되었습니다. 아래 키워드 생성으로 스토리 구축을 시작하세요.",
      selectDesign: "계속할 디자인을\n선택하세요",
      selectDesignHint: "패널에서 드래프트를 선택하거나 새 디자인을 생성하세요.",
      sidebar: { newDesign: "+ 새 디자인", designs: "디자인", noDesigns: "아직 디자인이 없습니다" },
      steps: {
        identity: {
          title: "작품에 이름 짓기",
          subtitle: "디자인에 제목과 본질을 담은 간결한 설명을 붙입니다. 바이어가 가장 먼저 보는 내용입니다.",
          designTitle: "디자인 제목",
          titleTip: "Caratzon 마켓플레이스에서의 공개 이름이 됩니다. 인상적이고 기억에 남는 이름을 — 바이어는 제목으로 먼저 브라우징합니다.",
          titlePlaceholder: "예: Lunette Drop Earrings",
          titleHint: "작품의 본질을 담은 이름을 선택하세요",
          description: "간결한 설명",
          descriptionPlaceholder: "피스와 캐릭터를 1~2문장으로.",
          descriptionHint: "카탈로그에 표시됩니다. 인상적이고 간결하게.",
        },
        pricing: {
          title: "가격 & 스펙",
          subtitle: "기본 라이선스 가격을 설정하고 소재 스펙을 입력하세요. 바이어의 비용 평가와 제조사의 견적 작성에 도움이 됩니다.",
          basePrice: "기본 가격 (USD)",
          basePriceTip: "라이선스 수수료입니다. 브랜드가 디자인을 사용하기 위해 지불하는 금액. 플랫폼 수수료 공제 후 80%를 수령합니다.",
          basePriceHint: "브랜드가 지불하는 디자인 라이선스 수수료. 80% 획득.",
          metalWeight: "금속 중량 (그램)",
          metalWeightTip: "대략적 중량이 제조사의 생산 비용 산정에 도움이 됩니다.",
          metalWeightHint: "디자인 내 금속의 대략적 중량",
          stoneCount: "스톤 수량",
          stoneCountHint: "피스에 세팅된 스톤의 총 개수",
          howPricingWorks: "가격 체계 안내:",
          howPricingBody: "기본 가격은 디자인 라이선스 수수료입니다. 제조 비용은 금속 중량·스톤·복잡성을 기반으로 인증 공장이 별도 견적합니다.",
        },
        licensing: {
          title: "IP 보호 선택",
          subtitle: "브랜드가 디자인을 어떻게 사용할 수 있는지 선택합니다. 각 티어마다 다른 보호 수준과 수익 모델을 제공합니다.",
          licensingTip: "IP 티어가 브랜드의 디자인 사용 조건을 결정합니다. 스탠다드는 복수 라이선스 가능; 독점은 한 브랜드에 일시적 권리; Vault는 풀 소유권 양도.",
          standard: "스탠다드 라이선스",
          standardDesc: "CAD는 Caratzon이 보유하며, NDA 하의 인증 제조사에게만 제공. 라이선스 수수료의 80%와 영구 로열티 획득.",
          standardDetail: "지속적 패시브 인컴을 원하는 디자이너에게 최적. 여러 브랜드가 동시 라이선스 가능.",
          exclusive: "시즈널 엠바고",
          exclusiveDesc: "한 브랜드가 12개월간 독점권 보유. 기간 중 디자인 비공개. 엠바고 종료 후 마켓 복귀.",
          exclusiveDetail: "독점성에 대한 프리미엄 가격. 브랜드가 엠바고 기간을 위해 더 높은 라이선스 수수료 지불.",
          vault: "풀 독점권",
          vaultDesc: "브랜드가 IP를 영구 취득. 프로덕션 CAD 직접 양도. 취득 가격의 80% 획득.",
          vaultDetail: "일시불 고액 거래. 디자인이 마켓플레이스에서 영구 삭제.",
          ipTransfer: "IP 양도",
        },
        assets: {
          title: "에셋 업로드",
          subtitle: "바이어와 제조사에 필요한 디자인 파일을 업로드합니다. 프로덕션 CAD는 보호되어 NDA 하의 인증 제조사만 열람 가능합니다.",
          assetsTip: "프로덕션 CAD는 바이어에게 공유되지 않습니다. Caratzon 인증 제조사만 엄격한 NDA 조건으로 접근합니다.",
          requiredFiles: "필수 파일",
          optionalFiles: "선택 — AI 스토리 품질 향상",
          vaultNotice: "풀 독점권.",
          vaultNoticeDetail: "CAD가 취득 브랜드에 직접 양도됩니다.",
          cadProtected: "CAD는 IP 보호됨.",
          cadProtectedDetail: "Caratzon 인증 제조사만 NDA 하에 수령.",
          cadLabel: "프로덕션 CAD",
          cadLabelVault: "IP 양도용 프로덕션 CAD",
          cadHint: ".stl · .3dm · .step — 소스 파일, 바이어에게 비공유",
          viewerLabel: "3D 뷰어 에셋",
          viewerHint: ".glb — 인터랙티브 바이어 미리보기용 CAD 익스포트",
          renderLabel: "2D 렌더 / 히어로 이미지",
          renderHint: ".jpg · .png · .webp — 메인 카탈로그 썸네일",
        },
        review: {
          title: "리뷰 & 생성",
          submit: "디자인 드래프트 생성",
          submitting: "생성 중…",
          identity: "아이덴티티",
          pricingSpecs: "가격 & 스펙",
          licensingTier: "라이선싱 티어",
          assets: "에셋",
          edit: "편집",
          uploaded: "업로드됨",
          missing: "미업로드",
          allRequired: "생성 전에 모든 필수 파일을 업로드해야 합니다.",
        },
      },
      workflow: {
        featured: "피처드",
        revisionRequired: "수정 필요",
        revisionHint: "위의 피드백에 대응한 후 다시 제출하세요.",
        noReason: "사유가 기재되지 않았습니다.",
        step01: "인스피레이션 키워드",
        step01Sub: "레퍼런스 사진을 업로드하고 컨텍스트를 추가합니다. AI가 무드·소재·테마를 분석하여 키워드 팔레트를 구성합니다.",
        additionalContext: "추가 컨텍스트",
        contextPlaceholder: "예: 아르데코 영향, 미드나이트 블루 사파이어…",
        generateKeywords: "키워드 생성",
        regenerateKeywords: "키워드 재생성",
        analysingRefs: "레퍼런스 분석 중…",
        step02: "키워드 선택",
        step02Sub: "이 작품의 본질을 가장 잘 포착하는 단어를 선택합니다 — AI 생성 스토리의 앵커가 됩니다.",
        saveKeywords: "선택 키워드 저장",
        saving: "저장 중…",
        step03: "스토리 생성",
        step03Sub: "소재·의도·숨겨진 의미를 기술합니다. AI가 카탈로그 페이지에 표시되는 내러티브를 작성합니다.",
        pieceDescription: "피스 설명",
        describePlaceholder: "디자인에 대해 기술하세요",
        materialsColors: "소재 & 색상",
        materialsPlaceholder: "파인 실버, 파베 다이아몬드…",
        initialSpark: "영감의 원천",
        sparkPlaceholder: "이미지, 기억, 분위기…",
        hiddenSymbolism: "숨겨진 상징",
        symbolismPlaceholder: "이 작품에 담긴 의미는?",
        generateStory: "스토리 생성",
        regenerateStory: "스토리 재생성",
        writingStory: "스토리 작성 중…",
        step04: "스토리 다듬기",
        step04Sub: "AI 드래프트를 리뷰합니다. 제목과 본문을 자유롭게 편집하세요 — 이 텍스트가 바이어가 카탈로그 페이지에서 읽는 내용입니다.",
        saveStory: "스토리 저장",
        step05: "쇼케이스 생성",
        step05Sub: "AI가 카탈로그 스프레드를 구성합니다 — 섹션 수·이미지 슬롯·헤드라인. 모두 스토리에서 생성됩니다.",
        currentLayout: "현재 레이아웃:",
        sections: "섹션",
        generatedImages: "생성 이미지",
        generatedImagesPlural: "생성 이미지",
        regenerateWarning: "재생성 시 현재 레이아웃이 교체됩니다.",
        generateShowcase: "쇼케이스 생성",
        regenerateShowcase: "쇼케이스 재생성",
        buildingShowcase: "쇼케이스 구성 중…",
        step06: "승인 신청",
        step06Sub: "관리자가 스토리와 쇼케이스를 리뷰할 때까지 편집이 잠깁니다. 결정이 내려지면 알림을 받습니다.",
        awaitingReview: "디자인이 리뷰 팀에 있습니다. 결정이 내려지면 알림을 받게 됩니다.",
        submitForApproval: "승인 신청",
        awaitingAdmin: "관리자 리뷰 대기 중…",
        published: "공개됨",
        submitting: "제출 중…",
        viewPublished: "공개된 스토리 보기",
      },
      upload: {
        dragOrClick: "여기에 드래그하거나",
        clickToBrowse: "클릭하여 찾아보기",
        maxSize: "최대 {size} MB",
        required: "필수",
        uploading: "{name} 업로드 중…",
        replace: "교체",
        retry: "재시도",
        demoPreLoaded: "데모 파일 미리 로드됨",
        photoRefs: "사진 레퍼런스",
        photoRefsHint: "— 선택, AI 품질 향상",
        dragOrClickPhotos: "드래그 또는 클릭하여 레퍼런스 사진 추가",
        upToPhotos: "최대 {max}장",
        remove: "삭제",
        inspirationNotes: "인스피레이션 노트",
        inspirationPlaceholder: "기억, 분위기, 감성적 단서, 특정 영감.",
      },
    },
    collections: {
      overline: "Designer Studio",
      title: "컬렉션",
      subtitle: "공개된 디자인을 AI 생성 에디토리얼 스토리가 포함된 큐레이티드 컬렉션으로 그룹핑합니다.",
      newCollection: "새 컬렉션",
      emptyTitle: "첫 컬렉션이 기다리고 있습니다",
      emptySubtitle: "2개 이상의 공개된 디자인을 선택하여 컬렉션을 만드세요. AI가 스토리를 엮어냅니다.",
      emptyHint: "또는 사이드바에서 기존 컬렉션을 선택",
      celebrationTitle: "컬렉션이 생성되었습니다!",
      celebrationSubtitle: "컬렉션이 스토리 워크플로를 위해 준비되었습니다. 아래 키워드 생성으로 시작하세요.",
      sidebar: { collections: "컬렉션", noCollections: "아직 컬렉션이 없습니다" },
      designPicker: {
        title: "컬렉션 피스",
        subtitle: "포함할 공개된 디자인을 선택합니다. 최소 2개 디자인이 필요합니다.",
        selected: "{count}개 선택됨",
        minRequired: "최소 2개 디자인을 선택하세요",
        saveDesigns: "피스 저장",
        saving: "저장 중…",
      },
      create: {
        title: "컬렉션 생성",
        collectionTitle: "컬렉션 제목",
        titlePlaceholder: "예: 미드나이트 오셔닉 시리즈",
        description: "간결한 설명",
        descriptionPlaceholder: "컬렉션 테마에 대한 몇 문장.",
        selectDesigns: "디자인 선택",
        selectDesignsHint: "공개된 디자인 중 2개 이상 선택",
        inspirationNote: "인스피레이션 노트",
        inspirationNotePlaceholder: "이 피스들을 연결하는 것은?",
        submit: "컬렉션 생성",
        submitting: "생성 중…",
      },
      draftNotInitialized: "컬렉션 드래프트가 초기화되지 않았습니다.",
      draftNotInitializedCta: "컬렉션으로 돌아가기",
      workflow: {
        step00: "컬렉션 피스",
        step00Sub: "이 컬렉션에 속하는 디자인을 관리합니다.",
        step01: "컬렉션 키워드",
        step01Sub: "AI가 모든 피스를 함께 분석하여 통합 테마·모티프·무드를 발견합니다.",
        additionalContext: "추가 컨텍스트",
        contextPlaceholder: "예: 공유 크리에이티브 시즌, 소재 팔레트, 감성적 실…",
        generateKeywords: "키워드 생성",
        regenerateKeywords: "키워드 재생성",
        analysingCollection: "컬렉션 분석 중…",
        step02: "키워드 선택",
        step02Sub: "이 컬렉션의 본질을 가장 잘 포착하는 단어를 선택합니다.",
        saveKeywords: "선택 키워드 저장",
        saving: "저장 중…",
        step03: "컬렉션 스토리 생성",
        step03Sub: "AI가 개별 디자인 스토리를 일관된 컬렉션 내러티브로 엮어냅니다.",
        generateStory: "스토리 생성",
        regenerateStory: "스토리 재생성",
        writingStory: "컬렉션 스토리 작성 중…",
        step04: "스토리 다듬기",
        step04Sub: "생성된 컬렉션 내러티브를 리뷰하고 편집합니다.",
        saveStory: "스토리 저장",
        step05: "쇼케이스 생성",
        step05Sub: "AI가 각 섹션에 이미지가 포함된 멀티 피스 에디토리얼 쇼케이스를 디자인합니다.",
        currentLayout: "현재 레이아웃:",
        sections: "섹션",
        generatedImages: "생성 이미지",
        generatedImagesPlural: "생성 이미지",
        regenerateWarning: "재생성 시 현재 레이아웃이 교체됩니다.",
        generateShowcase: "쇼케이스 생성",
        regenerateShowcase: "쇼케이스 재생성",
        buildingShowcase: "쇼케이스 구성 중…",
        step06: "승인 신청",
        step06Sub: "관리자가 컬렉션 스토리와 쇼케이스를 리뷰할 때까지 편집이 잠깁니다.",
        awaitingReview: "컬렉션이 리뷰 팀에 있습니다. 결정이 내려지면 알림을 받게 됩니다.",
        submitForApproval: "승인 신청",
        awaitingAdmin: "관리자 리뷰 대기 중…",
        published: "공개됨",
        submitting: "제출 중…",
        viewPublished: "공개된 컬렉션 보기",
        revisionRequired: "수정 필요",
        revisionHint: "위의 피드백에 대응한 후 다시 제출하세요.",
        noReason: "사유가 기재되지 않았습니다.",
        featured: "피처드",
      },
    },
    inbox: {
      title: "커스터마이징 수신함",
      subtitle: "조건 협상, 브리프 검토, 수정 CAD 파일 업로드.",
      overline: "Designer Studio",
      openRequests: "활성 요청",
      emptyTitle: "활성 요청 없음",
      emptySubtitle: "브랜드가 디자인에 커스터마이징 요청을 제출하면 여기에 표시됩니다.",
      selectRequest: "사이드바에서 요청을 선택하여 시작하세요.",
      thread: "스레드",
      noMessages: "아직 메시지가 없습니다.",
      updateStatus: "상태 업데이트",
      uploadRevision: "CAD 수정본 업로드",
      revisionHint: "CAD 수정본 업로드를 활성화하려면 요청을 진행 중으로 변경하세요.",
    },
    analytics: {
      overline: "Studio",
      title: "디자인 분석",
      subtitle: "디자인 성과를 추적합니다 — 조회수, 참여도, 도달 범위.",
      cards: { totalViews: "총 조회수", uniqueVisitors: "고유 방문자", avgTimeOnPage: "평균 체류 시간", quoteRequests: "견적 요청" },
      table: { design: "디자인", views: "조회수", uniqueViews: "고유", clicks: "클릭", avgTime: "평균 시간", scrollDepth: "스크롤 깊이" },
      drillDown: { viewsTrend: "조회수 추이 (30일)", clickBreakdown: "클릭 분석", referrerBreakdown: "유입 소스" },
      empty: "아직 분석 데이터가 없습니다. 디자인이 공개되고 조회되면 지표가 여기에 표시됩니다.",
      seconds: "초",
    },
  },

  // ─── Catalog ──────────────────────────────────────────────────────────────
  catalog: {
    overline: "트레이드 카탈로그",
    resultCount: "{count}건 결과",
    designCount: "{count}건 디자인",
    emptyTitle: "카탈로그를 큐레이션 중입니다",
    emptySubtitle: "디자이너들이 첫 컬렉션을 준비하고 있습니다. 곧 다시 확인해 주세요.",
    searchEmptyTitle: "일치하는 결과가 없습니다",
    searchEmptySubtitle: "다른 검색어를 시도하거나 전체 디자인을 브라우징하세요.",
    browseCta: "전체 디자인 보기",
  },

  // ─── Orders ───────────────────────────────────────────────────────────────
  orders: {
    overline: "트레이드 워크스페이스",
    title: "소싱 주문",
    subtitle: "활성 협상을 추적하고, 디자이너의 수정 3D 증빙을 리뷰하며, 생산 라우팅을 위한 제출을 승인합니다.",
    activeOrders: "활성 주문",
    emptyTitle: "아직 소싱 주문이 없습니다",
    emptyCta: "트레이드 카탈로그 보기 →",
    selectOrder: "협상 스레드를 보려면 주문을 선택하세요.",
    latestRevision: "최신 CAD 수정본 — 3D 미리보기",
    awaitingRevision: "디자이너의 첫 CAD 수정본을 기다리고 있습니다.",
    respondToDesigner: "디자이너에게 응답",
  },

  // ─── Admin ─────────────────────────────────────────────────────────────────
  admin: {
    brand: "Admin",
    nav: { workflows: "워크플로", publishing: "퍼블리싱", users: "사용자", academy: "아카데미", developer: "개발자", queues: "큐 모니터", catalog: "카탈로그", studio: "Studio", analytics: "분석", catalogAdmin: "카탈로그", catalogListings: "트레이드 리스팅", inspireAdmin: "Inspire", storyLab: "내 디자인" },
    users: {
      overline: "Admin",
      title: "사용자 관리",
      subtitle: "모든 역할의 사용자 계정을 생성, 편집, 관리합니다.",
      tabs: { users: "사용자", createNew: "신규 생성" },
      stats: { total: "총 사용자", active: "활성", suspended: "정지", designers: "디자이너" },
      search: "이름 또는 이메일로 검색…",
      allRoles: "전체 역할",
      noMatch: "필터에 일치하는 사용자가 없습니다.",
      columns: { name: "이름", email: "이메일", role: "역할", status: "상태", actions: "작업" },
      statusActive: "활성",
      statusSuspended: "정지",
      edit: "편집",
      suspend: "정지",
      reactivate: "재활성화",
      roles: { DESIGNER: "디자이너", B2B_BUYER: "브랜드 / 바이어", MANUFACTURER: "제조사", PRODUCTION_MANAGER: "생산 관리자", ADMIN: "Admin" },
      roleLabels: { DESIGNER: "디자이너", B2B_BUYER: "브랜드", MANUFACTURER: "공장", PRODUCTION_MANAGER: "PM", ADMIN: "Admin" },
      create: {
        title: "계정 생성",
        subtitle: "새 디자이너, 바이어, 제조사 또는 스태프 멤버를 추가합니다.",
        name: "이름",
        namePlaceholder: "예: Aria Atelier",
        email: "이메일",
        emailPlaceholder: "designer@example.com",
        password: "비밀번호",
        passwordPlaceholder: "최소 8자",
        role: "역할",
        submit: "계정 생성",
      },
      invite: {
        title: "초대 링크 전송",
        subtitle: "특정 이메일과 역할에 대한 가입 링크를 생성합니다. 7일간 유효합니다.",
        email: "이메일",
        emailPlaceholder: "new-designer@example.com",
        role: "역할",
        submit: "링크 생성",
        copied: "복사됨!",
        copy: "복사",
      },
      editDialog: {
        title: "사용자 편집",
        subtitle: "계정 정보와 역할 할당을 업데이트합니다.",
        name: "이름",
        email: "이메일",
        role: "역할",
        save: "변경 저장",
        cancel: "취소",
        stripe: "Stripe",
        accountActions: "계정 작업",
        resetPassword: "비밀번호 재설정",
        deleteAccount: "계정 삭제",
      },
      confirm: {
        suspendTitle: "사용자 정지",
        suspendDesc: "{name}을(를) 정지하시겠습니까? 즉시 플랫폼 접근이 차단됩니다.",
        suspendButton: "정지",
        reactivateTitle: "사용자 재활성화",
        reactivateDesc: "{name}을(를) 재활성화하시겠습니까? 전체 접근이 복구됩니다.",
        reactivateButton: "재활성화",
        deleteTitle: "사용자 삭제",
        deleteDesc: "{name}을(를) 영구 삭제하시겠습니까? 이 작업은 되돌릴 수 없으며 모든 관련 데이터가 제거됩니다.",
        deleteButton: "영구 삭제",
        cancel: "취소",
      },
      success: {
        created: "사용자 계정이 생성되었습니다.",
        updated: "사용자 정보가 업데이트되었습니다.",
        suspended: "사용자 계정이 정지되었습니다.",
        reactivated: "사용자 계정이 재활성화되었습니다.",
        deleted: "사용자 계정이 삭제되었습니다.",
        reset: "임시 비밀번호가 생성되었습니다.",
        invited: "초대 링크가 생성되었습니다.",
      },
      tempPasswordHint: "지금 복사하세요 — 다시 표시되지 않습니다.",
      dismiss: "닫기",
    },
    setup: {
      title: "플랫폼 설정",
      subtitle: "첫 관리자 계정을 생성하여 시작하세요.",
      steps: { authorize: "인증", credentials: "자격 증명", designer: "디자이너", confirm: "확인" },
      setupPasswordLabel: "설정 비밀번호",
      setupPasswordPlaceholder: "설정 비밀번호를 입력하세요",
      setupPasswordHint: "이 인스턴스에서 첫 관리자 계정을 생성하려면 설정 비밀번호가 필요합니다.",
      setupPasswordInvalid: "설정 비밀번호가 올바르지 않습니다.",
      emailLabel: "이메일",
      emailPlaceholder: "admin@company.com",
      nameLabel: "이름",
      namePlaceholder: "Jane Smith",
      passwordLabel: "비밀번호",
      passwordPlaceholder: "최소 8자",
      confirmPasswordLabel: "비밀번호 확인",
      confirmPasswordPlaceholder: "비밀번호 재입력",
      designerTitle: "디자이너 계정",
      designerSubtitle: "디자인 업로드를 시작할 수 있도록 첫 디자이너 계정을 생성합니다.",
      designerNameLabel: "디자이너 이름",
      designerNamePlaceholder: "Aria Atelier",
      designerEmailLabel: "디자이너 이메일",
      designerEmailPlaceholder: "designer@company.com",
      designerPasswordLabel: "디자이너 비밀번호",
      designerPasswordPlaceholder: "최소 8자",
      reviewTitle: "리뷰",
      reviewSubtitle: "생성될 계정을 확인하세요.",
      createAccount: "계정 생성",
      back: "뒤로",
      next: "다음",
      passwordMismatch: "비밀번호가 일치하지 않습니다.",
      passwordTooShort: "비밀번호는 최소 8자 이상이어야 합니다.",
    },
    analytics: {
      overline: "Admin",
      title: "플랫폼 분석",
      subtitle: "플랫폼 전체 디자인 참여 지표와 성과 인사이트.",
      cards: { totalViews: "총 조회수", uniqueVisitors: "고유 방문자", avgTimeOnPage: "평균 체류 시간", totalClicks: "총 클릭" },
      filters: { designer: "디자이너별 필터", dateRange: "기간", allDesigners: "전체 디자이너" },
      leaderboard: "최고 성과 디자인",
      zeroEngagement: "주의 필요 (참여도 제로)",
      table: { design: "디자인", designer: "디자이너", views: "조회수", uniqueViews: "고유", clicks: "클릭", avgTime: "평균 시간", scrollDepth: "스크롤 깊이" },
      drillDown: { viewsTrend: "조회수 추이", clickBreakdown: "클릭 분석", referrerBreakdown: "유입 소스" },
      empty: "아직 분석 데이터가 없습니다. 디자인이 공개되고 조회되면 지표가 여기에 표시됩니다.",
      seconds: "초",
    },
  },

  // ─── Chat / Messages ────────────────────────────────────────────────────────
  messages: {
    overline: "커뮤니케이션",
    title: "메시지",
    subtitle: "디자이너, 생산 관리자, 제조 파트너와의 대화.",
    emptyTitle: "아직 대화가 없습니다",
    emptyCta: "카탈로그를 브라우징하여 시작하세요",
    typeMessage: "메시지를 입력하세요...",
    send: "보내기",
    translate: "번역",
    translating: "번역 중...",
    loadMore: "이전 메시지 불러오기",
    typing: "입력 중...",
    typingMultiple: "입력 중...",
    today: "오늘",
    yesterday: "어제",
    unread: "읽지 않음",
    newConversation: "새 대화",
    participants: "참여자",
    quoteProposal: "견적 제안",
    statusUpdate: "상태 업데이트",
    attachFile: "파일 첨부",
    accept: "수락",
    counter: "카운터",
    connecting: "채팅 연결 중...",
    estimatedDays: "예상 배송",
    days: "일",
    messageDesigner: "디자이너에게 메시지",
  },

  // ─── Academy ──────────────────────────────────────────────────────────────
  academy: {
    meta: { title: "아카데미 — Caratzon", description: "디자이너와 마이크로 브랜드를 위한 주얼리 제조, 브랜드 구축, 산업 트렌드에 관한 전문 가이드." },
    hero: { title: "Caratzon 아카데미", subtitle: "주얼리 디자이너와 신규 브랜드를 위한 전문 지식 — CAD에서 커머스까지." },
    categories: { all: "전체", manufacturing: "제조", business: "비즈니스", trends: "트렌드", wholesale: "도매", brand: "브랜드", tech: "기술", operations: "운영" },
    readingTime: "분 소요",
    featured: "피처드",
    relatedArticles: "관련 기사",
    backToAcademy: "← 아카데미로 돌아가기",
    cta: { title: "디자인을 실현할 준비가 되셨나요?", description: "Caratzon은 독립 디자이너를 인증된 제조사와 연결하여 원활한 생산을 지원합니다.", button: "Caratzon에서 시작 →" },
    rss: "RSS 피드",
    noArticles: "아직 기사가 없습니다. 곧 다시 확인해 주세요!",
  },

  // ─── Newsroom ─────────────────────────────────────────────────────────────
  newsroom: {
    meta: { title: "뉴스룸 — Caratzon", description: "Caratzon의 최신 뉴스, 보도자료, 업데이트. 회사 공지, 제품 출시, 파트너십, 산업 인사이트." },
    hero: { issueLabel: "뉴스룸", title: "Caratzon 최신 소식", subtitle: "회사 뉴스, 보도자료, 제품 업데이트, 산업 인사이트." },
    categories: { all: "전체", company: "회사", product: "제품", partnership: "파트너십", industry: "산업", press_release: "보도자료" },
    readingTime: "분 소요",
    featured: "피처드",
    relatedArticles: "관련 기사",
    backToNewsroom: "← 뉴스룸으로 돌아가기",
    cta: { title: "디자인을 실현할 준비가 되셨나요?", description: "Caratzon은 독립 디자이너를 인증된 제조사와 연결하여 원활한 생산을 지원합니다.", button: "Caratzon에서 시작 →" },
    noArticles: "아직 기사가 없습니다. 곧 다시 확인해 주세요!",
    pressAssets: {
      meta: { title: "프레스 에셋 — Caratzon 뉴스룸", description: "보도 및 파트너를 위한 브랜드 로고, 가이드라인, 미디어 리소스. 에디토리얼용 Caratzon 브랜드 에셋을 다운로드하세요." },
      hero: { issueLabel: "프레스 에셋", title: "프레스 에셋", subtitle: "보도, 파트너, 에디토리얼용 브랜드 리소스. 아래에서 로고와 가이드라인을 다운로드하세요." },
      logos: { heading: "브랜드 로고", primary: "기본 로고 (라이트)", dark: "기본 로고 (다크)", icon: "아이콘만", stacked: "스택형" },
      guidelines: {
        heading: "브랜드 가이드라인",
        colorPalette: "컬러 팔레트",
        colorNotes: "기본 브랜드 액센트: 틸 사파이어(#005F6B). 텍스트에 딥 인디아 잉크. 배경에 웜 크림 벨럼. 골드 리프는 장식용으로만.",
        typography: "타이포그래피",
        typographyNotes: "디스플레이: Cormorant Garamond, Cormorant SC. 본문: Inter. 디스플레이 폰트는 헤드라인과 에디토리얼 악센트에; Inter는 UI와 본문 카피에.",
      },
      contact: { heading: "보도 문의", description: "미디어 문의, 프레스 키트, 인터뷰 요청:", contactLink: "일반 문의 →" },
    },
  },

  contentKit: {
    nav: "Caratzon Kit",
    meta: { title: "Caratzon Kit — Caratzon", description: "주얼리 디자인을 위한 완전한 Caratzon Kit을 생성하세요 — 모델 포토그래피, 룩북 촬영, 카탈로그 이미지, 소셜 캡션, 에디토리얼 스토리." },
    list: {
      title: "나의 Caratzon Kit",
      subtitle: "디자인에서 마케팅 준비 모델 포토그래피, 비주얼, 카피, 룩북을 생성합니다.",
      createNew: "새 Caratzon Kit",
      empty: "아직 Caratzon Kit이 없습니다. 첫 번째를 생성하여 시작하세요.",
      status: { draft: "드래프트", generating: "생성 중", ready: "준비 완료", failed: "실패" },
    },
    wizard: {
      step1: "소스 선택",
      step2: "비주얼 생성",
      step3: "카피 생성",
      step4: "룩북 생성",
      step5: "퍼블리시 & 공유",
      source: { title: "디자인 소스를 선택하세요", fromCatalog: "카탈로그에서", fromCatalogDesc: "마켓플레이스에서 라이선스한 디자인을 선택합니다.", uploadNew: "이미지 업로드", uploadNewDesc: "자체 주얼리 사진을 업로드합니다.", titleLabel: "피스 제목", descriptionLabel: "설명", next: "비주얼 생성" },
      visuals: { title: "생성된 비주얼", subtitle: "AI 향상 상품 촬영과 라이프스타일 룩북 이미지.", generating: "비주얼 생성 중...", regenerate: "재생성", acceptAll: "수락 & 계속", catalogShot: "상품 촬영", lookbookShot: "라이프스타일" },
      copy: { title: "마케팅 카피", subtitle: "AI 생성 상품 설명과 소셜 캡션.", generating: "카피 작성 중...", productDescription: "상품 설명", brandStory: "브랜드 스토리", socialCaptions: "소셜 캡션", regenerate: "카피 재생성", next: "룩북 생성" },
      lookbook: { title: "룩북 미리보기", subtitle: "다운로드 준비 완료된 AI 조립 룩북.", generating: "룩북 조립 중...", template: "템플릿 스타일", regenerate: "다른 스타일로 재생성", templates: { minimal: "미니멀", editorial: "에디토리얼", bold: "볼드", organic: "오가닉" }, next: "퍼블리시 & 공유" },
      publish: {
        title: "퍼블리시 & 공유",
        subtitle: "Caratzon Kit을 다운로드하거나 Discovery에 공개합니다.",
        download: "다운로드",
        downloadAll: "전체 키트 다운로드",
        downloadImages: "이미지 다운로드 (ZIP)",
        downloadCopy: "카피 다운로드 (TXT)",
        downloadPdf: "룩북 다운로드 (PDF)",
        publish: "Discovery에 공개",
        publishDesc: "Caratzon에서 디스커버리 페이지를 공개합니다.",
        slugLabel: "디스커버리 URL",
        categoryLabel: "카테고리",
        materialsLabel: "소재",
        keywordsLabel: "스타일 키워드",
        heroLabel: "히어로 이미지",
        published: "공개됨!",
        viewDiscovery: "디스커버리 페이지 보기",
      },
    },
    upsell: { title: "Studio 크레딧이 소진되었습니다", body: "Source로 업그레이드하면 매월 1,500 Studio 크레딧 — 15개 Caratzon Kit에 충분합니다. 또는 크레딧 팩으로 즉시 충전하세요.", cta: "요금제 보기" },
  },

  concierge: {
    title: "Caratzon 컨시어지",
    subtitle: "B2B 주얼리 소싱 어시스턴트",
    welcome: "환영합니다! 디자인, 라이선싱 티어, 제조 또는 Caratzon에 대해 무엇이든 물어보세요.",
    inputPlaceholder: "디자인, 라이선싱, 가격에 대해 질문하세요...",
    disclaimer: "AI 기반. 응답이 완전히 정확하지 않을 수 있습니다.",
    quickActions: [
      "라이선싱은 어떻게 작동하나요?",
      "디자이너입니다 — 어떻게 참여하나요?",
      "요금제가 어떻게 되나요?",
      "담당자와 대화하고 싶습니다",
    ],
    leadForm: {
      title: "팀과 연결하기",
      description: "정보를 공유해 주시면 24시간 이내에 팀이 후속 조치합니다.",
      name: "이름",
      email: "비즈니스 이메일",
      company: "회사 (선택)",
      message: "필요한 도움을 간략히 설명해 주세요...",
      submit: "팀과 연결해 주세요",
      successTitle: "연결되었습니다",
      success: "24시간 이내에 팀이 연락드리겠습니다. 그동안 여기에서 계속 채팅하실 수 있습니다.",
    },
    handoffConfirm: {
      title: "핸드오프 요청됨",
      description: "요청 세부 정보를 팀에 전달했습니다. 담당자가 곧 연락드리겠습니다. 여기에서 계속 채팅하시거나 trade@caratzon.com으로 직접 연락하실 수 있습니다.",
    },
  },

  inspire: {
    meta: { title: "Inspire — Caratzon", description: "전 세계 디자이너와 브랜드의 멋진 주얼리 디자인을 탐색하세요." },
    explore: { title: "Inspire", subtitle: "전 세계 크리에이터의 주얼리 디자인을 탐색합니다.", allCategories: "전체", noResults: "이 카테고리에 피스가 없습니다." },
    profile: { pieces: "피스", totalViews: "총 조회수" },
    detail: {
      by: "by",
      views: "조회수",
      materials: "소재",
      share: "공유",
      viewProfile: "프로필 보기",
      cta: { title: "나만의 Inspire 페이지 만들기", body: "디자이너로 참여하여 AI 생성 룩북, 에디토리얼 스토리, 전용 프로필로 작품을 쇼케이스하세요 — 모두 Studio에서.", button: "크리에이팅 시작" },
    },
    saved: { title: "저장된 피스", empty: "아직 저장된 피스가 없습니다.", emptySubtitle: "Inspire를 브라우징하여 즐겨찾기를 찾으세요.", backToInspire: "Inspire로 돌아가기" },
    creatorOnboarding: {
      meta: { title: "Inspire 크리에이터로 참여 · Caratzon", description: "크래프트, 스토리, 비전에 대해 알려주세요 — 세계에 소개할 수 있도록." },
      header: "Inspire · 크리에이터 온보딩",
      stepOf: "단계 {step} / {total}",
      back: "← 뒤로",
      continue: "계속 →",
      steps: { profile: "프로필", styleTools: "스타일 & 도구", creativeDna: "크리에이티브 DNA", philosophy: "철학", innerWorld: "내면 세계", production: "프로덕션", review: "리뷰" },
      profile: {
        title: "디자이너 프로필",
        subtitle: "바이어와 PM이 플랫폼에서 보는 프로필입니다.",
        firstName: "이름 *",
        lastName: "성 *",
        studioName: "스튜디오 / 브랜드명",
        studioPlaceholder: "예: Studio Noir, Aria Atelier",
        location: "위치 / 도시",
        locationPlaceholder: "예: 서울, 대한민국",
        photo: "프로필 사진",
        photoOptional: "(선택 — AI 향상)",
        photoHint: "헤드샷 또는 캔디드 사진을 업로드하세요. AI가 따뜻한 스튜디오 조명과 깔끔한 배경으로 향상시킵니다.",
        photoEnhancing: "AI로 사진 향상 중…",
        photoSaved: "사진 향상 & 저장 완료 ✓",
        photoChange: "변경",
        photoUpload: "업로드",
      },
      style: {
        title: "미학 & 도구",
        subtitle: "적합한 바이어 대상과 매칭하기 위해 사용합니다.",
        aestheticLabel: "디자인 미학을 어떻게 설명하시겠습니까? *",
        aestheticHint: "해당하는 항목을 모두 선택",
        designStyleLabels: {
          "Minimalist": "미니멀리스트",
          "Vintage-inspired": "빈티지 영감",
          "Bridal/Engagement": "브라이들 / 약혼",
          "Men's": "남성용",
          "Bold/Chunky": "볼드 / 청키",
          "Trend-driven Fashion": "트렌드 패션",
          "Everyday Fine": "에브리데이 파인",
          "Maximalist": "맥시멀리스트",
        } as Record<string, string>,
        cadLabel: "주로 사용하는 CAD 소프트웨어는? *",
        cadHint: "해당하는 항목을 모두 선택",
        formatsLabel: "제공 가능한 파일 포맷은?",
        formatsHint: "해당하는 항목을 모두 선택",
      },
      creative: {
        title: "크리에이티브 DNA",
        subtitle: "크래프트의 뿌리를 이해하는 데 도움이 됩니다. 이 답변이 AI가 바이어에게 전하는 스토리를 형성합니다.",
        originLabel: "어떻게 주얼리 디자이너가 되었나요? *",
        originHint: "당신의 오리진 스토리 — 이 크래프트에 끌린 계기는? 어린 시절 기억, 멘토, 전환점?",
        originPlaceholder: "할머니가 자이푸르의 작업실에서 실크에 시드 진주를 꿰는 것을 보며 자랐습니다. 각 비드에 빛이 맺히는 모습이 마법 같았습니다…",
        materialsLabel: "가장 연결감을 느끼는 소재는?",
        materialsHint: "공감하는 항목을 모두 선택",
        signatureMaterialLabels: {
          "Gold (yellow, rose, white)": "골드 (옐로우, 로즈, 화이트)",
          "Platinum": "플래티넘",
          "Silver / Sterling": "실버 / 스털링",
          "Titanium": "티타늄",
          "Enamel": "에나멜",
          "Precious gemstones": "귀석",
          "Semi-precious stones": "반귀석",
          "Pearls": "진주",
          "Diamonds": "다이아몬드",
          "Wood / organic materials": "목재 / 유기 소재",
          "Recycled metals": "재활용 금속",
          "Lab-grown stones": "랩그로운 스톤",
          "Mixed media": "믹스드 미디어",
        } as Record<string, string>,
        ritualsLabel: "크리에이티브 리추얼을 설명해 주세요",
        ritualsHint: "작업 프로세스는 어떤가요? 먼저 스케치하나요, 바로 CAD로 가나요, 왁스를 다루나요? 어디에서 작업하나요?",
        ritualsPlaceholder: "항상 질감 있는 종이 위에 수채화 스케치로 시작합니다. 화면을 터치하기 전에 무게와 흐름을 느껴야 합니다…",
        culturalLabel: "작품에 영향을 미치는 문화적·역사적 요소는?",
        culturalHint: "전통, 지역, 시대, 예술 운동 — 비주얼 언어를 형성하는 모든 것.",
        culturalPlaceholder: "아르데코 기하학, 무굴 꽃 모티프, 일본 와비사비의 불완전함…",
      },
      philosophy: {
        title: "디자인 철학",
        subtitle: "크래프트에 대한 사고 방식 — 텐션, 신념, 선택을 드러내는 질문입니다.",
        oneWordLabel: "디자인 철학을 한 단어로 표현한다면? *",
        oneWordPlaceholder: "예: 텐션, 경외, 반항, 유동",
        feelingLabel: "당신의 주얼리를 착용한 사람은 무엇을 느껴야 하나요? *",
        feelingHint: "어떻게 보여야 하는지가 아닙니다 — 피스가 어떤 감정 상태를 불러일으켜야 하는지.",
        feelingPlaceholder: "비밀을 간직한 것처럼 — 소중하고 온전히 자신만의 무언가. 스스로를 드러내지 않아도 되는 조용한 자신감.",
        tensionLabel: "작품을 정의하는 크리에이티브 텐션은?",
        tensionHint: "모든 디자이너는 트레이드오프를 다룹니다. 장식 vs. 절제? 전통 vs. 파격? 완벽 vs. 날것?",
        tensionPlaceholder: "정밀함과 불완전함 사이에서 늘 끌립니다. 모든 선이 의도적이길 원하지만, 금속이 저를 놀라게 할 여지를 남깁니다…",
        opinionLabel: "주얼리 디자인에 대해 가지고 있는 비주류 견해는?",
        opinionHint: "대부분의 업계 사람들이 동의하지 않을 당신의 믿음.",
        opinionPlaceholder: "대칭은 과대평가되었다고 생각합니다. 가장 아름다운 피스는 의도적인 비대칭이 있어서 살아있는 느낌을 줍니다…",
      },
      innerWorld: {
        title: "내면 세계",
        subtitle: "이 깊은 질문들이 AI가 작업대 너머의 당신을 이해하도록 도와 — 디자이너 스토리가 진정성 있게 느껴지도록.",
        inspirationLabel: "주얼리 이외에 디자인에 영감을 주는 것은? *",
        inspirationHint: "건축, 음악, 자연, 음식, 영화, 수학, 도시, 감정 — 무엇이든.",
        inspirationPlaceholder: "브루탈리즘 콘크리트에 오후 햇살이 닿는 방식. 앵무조개 껍데기의 수학적 나선. 일본 석정원의 고요함…",
        emotionalLabel: "디자인하는 것이 채워주는 감정적 필요는?",
        emotionalHint: "왜 계속 창작하나요? 그것이 없으면 삶에서 무엇이 빠질까요?",
        emotionalPlaceholder: "마음이 고요해지는 유일한 시간입니다. 디자인에 깊이 몰입하면 소음이 멈추고 피스와 나만 남습니다…",
        legacyLabel: "20년 후 작품군이 당신에 대해 무엇을 말하길 원하나요?",
        legacyHint: "레거시 비전 — 당신이 만드는 모든 것을 연결하는 실은?",
        legacyPlaceholder: "사람들이 그냥 착용한 것이 아니라 간직한 피스를 만들었다는 것. 가치가 아닌 의미를 위해 물려주는 물건…",
        metaphorLabel: "디자인 실천이 장소라면, 어떤 모습일까요?",
        metaphorHint: "크리에이티브 공간에 대한 은유 — 현실이든 상상이든.",
        metaphorPlaceholder: "절벽 끝의 유리 작업실. 정밀한 도구, 거친 풍경. 안은 질서정연; 밖은 길들여지지 않은…",
      },
      production: {
        title: "프로덕션 준비도",
        subtitle: "제조사는 물리적 생산에 최적화된 CAD 파일이 필요합니다 — 프롱 두께, 금속 중량, 수축 공차.",
        cadOptimisedLabel: "CAD 파일이 물리적 제조에 완전히 최적화되어 있나요?",
        optionYes: "예 — 제조용으로 완전 최적화됨",
        optionMostly: "대체로 — 사소한 조정이 필요할 수 있음",
        optionNo: "아니오 — 렌더/시각화 전용 디자인",
        optionHelp: "파일 최적화에 도움이 필요합니다",
        portfolioLabel: "포트폴리오 / 웹사이트",
        portfolioPlaceholder: "https://yourportfolio.com",
        socialLabel: "소셜 프로필",
        socialOptional: "(선택 — Inspire 페이지에 표시)",
        instagramPlaceholder: "@yourstudio 또는 https://instagram.com/yourstudio",
        pinterestPlaceholder: "@yourstudio 또는 https://pinterest.com/yourstudio",
        tiktokPlaceholder: "@yourstudio 또는 https://tiktok.com/@yourstudio",
        sampleUploadsTitle: "샘플 업로드",
        sampleUploadsBody: "프로필 완성 후 Studio 대시보드에서 직접 CAD 렌더와 완성품 사진을 업로드할 수 있습니다.",
      },
      review: {
        title: "프로필 검토",
        subtitle: "모든 것이 맞나요? 승인 후 Studio 설정에서 업데이트할 수 있습니다.",
        aiEnhanced: "AI 향상 초상화",
        labels: { name: "이름", studio: "스튜디오", location: "위치", aesthetic: "미학", cadSoftware: "CAD 소프트웨어", fileFormats: "파일 포맷", productionReady: "프로덕션 준비", portfolio: "포트폴리오", instagram: "Instagram", pinterest: "Pinterest", tiktok: "TikTok" },
        creativeProfile: "크리에이티브 프로필",
        creativeLabels: { originStory: "오리진 스토리", materials: "소재", philosophy: "철학", wearerFeels: "착용자 감정", inspiration: "영감", legacy: "레거시" },
        storyGeneration: { title: "디자이너 스토리 생성", subtitle: "AI가 설문 답변으로 매력적인 내러티브를 작성합니다 — Inspire 페이지에 바로 공개 가능.", button: "내 스토리 생성", loading: "내러티브 작성 중…", error: "스토리 생성 실패", tryAgain: "다시 시도", refineLater: "승인 후 Studio 대시보드에서 이 스토리를 다듬을 수 있습니다." },
        terms: { prefix: "위 정보가 정확함을 확인하며 Caratzon의", termsLink: "디자이너 이용약관", and: "및", privacyLink: "개인정보 처리방침" },
        submit: "신청서 제출 →",
        submitting: "제출 중…",
      },
      errors: { nameRequired: "이름과 성을 입력해 주세요.", agreeRequired: "디자이너 이용약관에 동의해야 진행할 수 있습니다.", submissionFailed: "신청서 제출 중 오류가 발생했습니다. 다시 시도해 주세요." },
      pending: {
        meta: { title: "신청 검토 중 · Caratzon", description: "디자이너 신청이 팀에서 검토 중입니다." },
        overline: "프라이빗 베타",
        thankYou: "감사합니다, {name}",
        thankYouGeneric: "감사합니다",
        submitted: "신청이 성공적으로 제출되었습니다. Caratzon은 현재",
        privateBeta: "프라이빗 베타",
        submittedSuffix: " 상태이며, 팀이 모든 디자이너 신청을 개별적으로 검토합니다.",
        emailNotice: "계정이 승인되면 이메일로 알려드리겠습니다. 일반적으로 1~2영업일 소요됩니다.",
        backToHome: "홈으로 돌아가기",
        exploreAcademy: "아카데미 탐색 →",
        rejected: {
          overline: "신청 업데이트",
          heading: "현재 신청을 승인할 수 없습니다",
          body: "Caratzon에 관심을 가져주셔서 감사합니다. 안타깝게도 현재 신청을 승인할 수 없습니다. 오류라고 생각하시거나 추가 정보를 제공하고 싶으시면 팀에 연락해 주세요.",
          feedbackLabel: "팀의 피드백",
          contactUs: "문의하기",
          reapply: "재신청",
          reapplying: "제출 중…",
        },
      },
    },
  },

  caraChat: {
    empty: { title: "Cara를 만나보세요", description: "당신의 AI 디자인 어시스턴트 — 마스터 주얼러이자 메커니컬 엔지니어. 비전을 설명하거나, 스케치를 업로드하거나, 인스피레이션 이미지를 드롭하여 시작하세요.", sketchToConcept: "스케치 → 콘셉트", photoToDesign: "사진 → 디자인", ideaToRender: "아이디어 → 렌더" },
    placeholder: { initial: "비전을 설명하거나, 스케치를 업로드하거나, 인스피레이션 이미지를 드롭하세요…", direction: "Cara에게 방향을 알려주세요 — 소재, 스타일, 시대…", adjust: "Cara에게 디자인 조정을 요청하거나, 구조 분석을 요청하세요…", finalize: "마무리할 준비가 되셨나요? Cara에게 카탈로그 렌더와 BOM 생성을 요청하세요." },
    dragOverlay: "스케치 또는 레퍼런스 이미지 드롭",
    scrollPill: "↓ 새 메시지",
    sketchFallback: "스케치 / 레퍼런스 이미지입니다. 분석해 주세요.",
    error: "오류가 발생했습니다. 다시 시도해 주세요.",
    regenerate: "재생성",
    suggestions: { custom: "기타…", customPlaceholder: "답변을 입력하세요…", send: "보내기" },
    editMessage: "메시지 편집",
    editSend: "보내기",
    editCancel: "취소",
    download: "다운로드",
    copyMessage: "메시지 복사",
    justNow: "방금",
    minutesAgo: "{n}분 전",
    toolLabels: { generate_mood_board: "무드보드", generate_concept_sketch: "콘셉트 스케치", generate_design_render: "디자인 렌더", adjust_design: "조정된 렌더", generate_catalog_render: "카탈로그 렌더", render_from_sketch: "스케치 콘셉트", analyze_sketch: "스케치 분석", analyze_inspiration: "인스피레이션 분석", reference_lookup: "레퍼런스 조회", structural_analysis: "구조 분석", calculate_bom: "부품표", material_recommendation: "소재 분석", generate_turntable_video: "턴테이블 비디오", generate_lookbook_image: "룩북 이미지", present_design_specs: "디자인 스펙", finalize_design: "최종 디자인", share_to_gallery: "갤러리 공유" },
    toolLoading: { rendering: "렌더링 중…", sketchingConcept: "콘셉트 스케칭 중…", analyzingSketch: "스케치 분석 중…", analyzingInspiration: "인스피레이션 분석 중…", lookingUpReference: "레퍼런스 데이터 조회 중…", generatingVideo: "비디오 생성 중 — 최대 1분 소요…", stylingLookbook: "룩북 스타일링 중…", structuralAnalysis: "제조 가능성 시뮬레이션 실행 중…", materialCheck: "소재 호환성 확인 중…", calculatingBom: "부품표 계산 중…", sharingToGallery: "갤러리에 공유 중…", preparingSpecs: "스펙 준비 중…", finalizingDesign: "디자인 최종화 중…", processing: "처리 중…" },
    toolCards: {
      moodBoard: "무드보드", conceptSketch: "콘셉트 스케치", refinedSketch: "정교화된 스케치", turntablePreview: "턴테이블 미리보기", sketchAnalysis: "스케치 분석", inspirationAnalysis: "인스피레이션 분석", structuralAnalysis: "구조 분석", billOfMaterials: "부품표", materialAnalysis: "소재 분석", designFinalized: "디자인 최종화 완료", sharedToGallery: "갤러리에 공유됨", errorFallback: "오류가 발생했습니다.", designRender: "디자인 렌더", updatedRender: "업데이트된 렌더", catalogRender: "카탈로그 렌더", conceptFromSketch: "스케치에서 콘셉트", lookbookLabel: "룩북", saveToStudio: "Studio에 저장", savedOpenStudio: "저장됨 — Studio에서 열기", submitBrief: "디자인 브리프 제출", briefSubmitted: "브리프 제출됨 — 주문 보기", designTitlePlaceholder: "디자인 제목…", saving: "저장 중…", save: "저장", cancel: "취소",
      structural: { manufacturabilityReport: "제조 가능성 보고서", areasAnalyzed: "{n}개 영역 분석됨", areasAnalyzedPlural: "{n}개 영역 분석됨", issuesFound: "문제 발견", warnings: "경고", allClear: "이상 없음", pass: "통과", warning: "경고", warningPlural: "경고", critical: "심각", recommendationLabel: "권장 사항:", addressCritical: "캐스팅 진행 전에 심각한 문제를 해결하세요.", reviewWarnings: "위 경고를 검토하세요 — 명시된 조정으로 제작 가능합니다." },
      bom: { costBreakdown: "비용 분석", lineItems: "{n}개 항목", lineItemsPlural: "{n}개 항목", item: "항목", qty: "수량", estCost: "예상 비용", laborSetting: "인건비 & 세팅", totalEstimate: "총 예상" },
      material: { compatibilityCheck: "호환성 확인", compatible: "호환", concerns: "우려", concernsLabel: "우려 사항", recommendationsLabel: "권장 사항", allCompatible: "모든 소재가 생산에 호환됩니다." },
      sketch: { sketchInterpretation: "스케치 해석", needsInput: "입력 필요", stones: "스톤", filigree: "필리그리", engraving: "인그레이빙", designNotes: "디자인 노트", needsClarification: "명확화 필요" },
      inspiration: { inspirationBreakdown: "인스피레이션 분석", colorPalette: "컬러 팔레트", motifs: "모티프", textures: "텍스처", suggestedFinishes: "권장 피니시", suggestedForms: "권장 형태" },
      designSpecs: { weight: "중량", stones: "스톤", stoneSingular: "스톤", finish: "피니시", stonesLabel: "스톤", dimensions: "치수", manufacturable: "제조 가능", manufacturabilityConcerns: "제조 가능성 우려" },
      finalize: { productionReady: "프로덕션 레디", metal: "금속", stones: "스톤", stoneSingular: "스톤", estRetail: "예상 소매가", cadHandoff: "CAD 핸드오프, 캐스팅 또는 벤더 견적 준비 완료" },
      gallery: { nowInGallery: "갤러리에 등록됨", storyGenerating: "디자인 스토리가 생성 중입니다 — 갤러리 탭에서 확인하세요." },
      reference: { title: "레퍼런스 데이터", casting: "캐스팅 스펙", metals: "금속 속성", stones: "스톤 내구성", settings: "세팅 호환성" },
    },
    starters: [
      { label: "솔리테어 링 디자인", text: "18k 로즈 골드에 1ct 오벌 다이아몬드를 사용한 모던 솔리테어 약혼 반지를 디자인하세요" },
      { label: "아르데코 칵테일 링", text: "에메랄드와 오닉스를 사용한 아르데코 기하학 영감의 볼드한 칵테일 링을 만드세요" },
      { label: "미니멀 테니스 브레이슬릿", text: "플래티넘에 사파이어와 다이아몬드가 번갈아 세팅된 미니멀 테니스 브레이슬릿을 디자인하세요" },
    ],
    buyerOverrides: {
      empty: { title: "Cara와 디자인하기", description: "찾고 있는 주얼리를 설명하세요 — 스타일, 소재, 용도 — Cara가 프로덕션 레디 렌더로 비전을 구현합니다." },
      placeholder: { initial: "찾고 있는 주얼리를 설명하세요 — 스타일, 소재, 용도…", direction: "선호 사항을 더 알려주세요 — 소재, 예산, 수량…", adjust: "Cara에게 디자인 조정을 요청하거나, 다른 변형을 탐색하세요…", finalize: "디자인이 마음에 드시나요? 프로덕션을 위한 디자인 브리프로 제출하세요." },
      starters: [
        { label: "커스텀 약혼 반지", text: "커스텀 약혼 반지를 찾고 있습니다 — 오벌 다이아몬드, 모던 세팅, 로즈 골드" },
        { label: "브랜디드 컬렉션 피스", text: "브랜드의 기하학적 모티프를 활용한 시그니처 펜던트가 필요합니다 — 미니멀, 14k 골드, 소매가 $200 이하" },
        { label: "웨딩 밴드 세트", text: "플래티넘으로 섬세한 디테일이 있는 매칭 커플 웨딩 밴드 세트를 디자인하세요" },
      ],
    },
  },

  // ─── Pricing page ───────────────────────────────────────────────────────
  pricing: {
    meta: { title: "요금제 · Caratzon", description: "Caratzon 브랜드를 위한 투명한 요금제 — 프리엔지니어드 주얼리 디자인 소싱, AI 마케팅 키트 생성, 지속가능 제조." },
    hero: { overline: "브랜드 · 트레이드 멤버십", heading: "디자인당 $2,500 지불을 멈추세요.", headingEm: "$0부터 시작하세요.", body: "멤버십으로 전체 카탈로그, AI 기반 마케팅 도구, 독립 디자이너로부터의 직접 소싱을 언락합니다. 디자인 라이선스는 별도 — 생산하기로 선택한 것만 지불합니다." },
    socialProof: ["48+ 프리엔지니어드 디자인", "6개국의 디자이너", "3분 이내 AI 키트 생성", "유닛 1부터 수익"],
    billing: { monthly: "월간", annual: "연간", savingActive: "~17% 절약 중", savingPrompt: "연간 ~17% 절약" },
    tiers: {
      explorer: { name: "Discover", tagline: "가능성을 확인하세요", description: "카탈로그를 브라우징하고 즐겨찾기를 저장하며 200 웰컴 크레딧으로 첫 AI 마케팅 키트를 생성하세요 — 무료, 카드 불필요.", features: ["전체 카탈로그 브라우징 — 모든 디자이너, 모든 피스", "디자인을 숏리스트에 저장", "200 웰컴 Studio 크레딧 (1회)", "2개 Essentials Kit 생성으로 품질 직접 확인", "수량 선택 — CAD에서 배송까지 나머지는 저희가 처리"], cta: "무료로 시작" },
      professional: { name: "Source", tagline: "디자이너에 직접 접근", description: "풀 소싱 접근. 디자이너와 직접 소통. 월 1,500 크레딧 — 15개 완전 마케팅 키트 또는 150개 퀵턴 카탈로그 촬영에 충분.", mostPopular: "가장 인기", features: ["Discover의 모든 것 포함, 추가:", "매월 1,500 Studio 크레딧", "디자이너와 직접 소싱 — 중간 유통 없음", "비공개 메시징으로 라이선싱 조건 협상", "AI 마케팅 키트: 라이프스타일 촬영, 룩북, SNS 에셋 — 수 분 내 준비", "스탠다드 + 독점 라이선싱 티어", "금속·스톤·스타일·가격·가용성으로 필터"], cta: "Source 시작" },
      atelier: { name: "Atelier", tagline: "프라이빗 디자인 워크숍", description: "Vault 라이선싱을 통한 완전 IP 양도. 전담 생산 관리자가 CAD에서 배송까지 모든 것을 처리. 월 5,000 크레딧.", features: ["Source의 모든 것 포함, 추가:", "Vault 라이선싱 — 풀 IP + CAD 파일 양도", "매월 5,000 Studio 크레딧", "전담 생산 관리자 — 단일 연락 창구", "우선 지원 — 다이렉트 라인, 당일 응답", "공개 전 새 디자이너 조기 접근", "커스텀 소싱 브리프 — 필요한 것을 알려주시면 찾아드립니다", "멀티 SKU 주문 볼륨 라이선싱 조건", "Cara AI — AI 어시스턴스로 오리지널 주얼리 디자인"], cta: "Atelier 시작" },
      enterprise: { name: "Enterprise", tagline: "제한 없이 확장", description: "리테일 체인과 대형 유통사를 위해. 커스텀 크레딧 볼륨, API 통합, 화이트 글러브 생산 관리, 전담 어카운트 팀.", customLabel: "맞춤형", customSublabel: "볼륨과 요구사항에 맞게 조정", features: ["Atelier의 모든 것 포함, 추가:", "커스텀 Studio 크레딧 볼륨 — 카탈로그 속도에 맞춤", "API 접근 — 카탈로그, 키트, 주문 관리", "멀티 팩토리 화이트 글러브 생산 관리", "분기별 비즈니스 리뷰가 포함된 전담 어카운트 팀", "커스텀 SLA, 온보딩, 트레이닝", "역할 기반 권한의 멀티시트 팀 접근", "협상된 라이선싱 조건 및 볼륨 할인", "우선 제조 대기열 — 주문이 먼저 출하"], cta: "팀에 문의" },
    },
    priceDisplay: { free: "무료", freeSublabel: "카드 불필요", perMonth: "/월", saveBadge: "(~17% 절약)", orAnnualOption: "또는 {amount}/년", billedAnnually: "연간 {amount} 청구", licensesSeparate: "디자인 라이선스 별도 청구" },
    getStarted: "시작하기",
    studioCredits: { overline: "Studio 크레딧", heading: "작업당 고정 비용. 놀라움 없이.", body: "카탈로그 포토그래피부터 Cara와의 디자인 상담까지 — 모든 AI 작업은 월간 할당량에서 고정 크레딧 수를 사용합니다. 더 필요하신가요? 탑업 팩이 즉시 이용 가능하며 만료되지 않습니다.", link: "크레딧 비용 & 탑업 팩 보기 →" },
    roi: {
      overline: "숫자로 보기",
      heading: "새 SKU 런칭에 실제로 얼마나 드나요.",
      body: "기존 소싱은 숨겨진 비용과 수개월의 리드 타임에 마진이 묻힙니다. Caratzon은 전체 파이프라인을 단축합니다.",
      traditionalLabel: "기존 소싱",
      caratzonLabel: "Caratzon",
      rows: [
        { metric: "시장 투입 시간", traditional: "4–6개월", caratzon: "3주" },
        { metric: "R&D & CAD 모델링", traditional: "$800 – $2,500+", caratzon: "$0 — 프리엔지니어드" },
        { metric: "물리적 샘플링", traditional: "라운드당 $500+", caratzon: "$0 — AI 렌더 미리보기" },
        { metric: "마케팅 포토그래피", traditional: "캠페인당 $2,000+", caratzon: "Studio 크레딧으로 ~$7" },
        { metric: "손익분기점", traditional: "~50유닛 판매", caratzon: "유닛 1" },
      ],
      note: "프리엔지니어드 CAD 파일. 디지털 트윈 샘플링. AI 파워드 포토그래피. Caratzon의 모든 디자인은 첫 유닛부터 수익성이 있습니다.",
    },
    faq: {
      overline: "자주 묻는 질문",
      heading: "알아야 할 모든 것.",
      items: [
        { q: "플랫폼 수수료와 디자인 라이선스에 포함된 것은?", a: "멤버십으로 카탈로그 접근, AI 기반 마케팅 도구(Studio 크레딧), 직접 소싱 기능을 얻습니다. 디자인 라이선스는 별도 — 생산하기로 선택한 디자인에만 지불합니다. 멤버십은 도구 상자; 라이선스는 재료라고 생각하세요." },
        { q: "미사용 Studio 크레딧은 어떻게 되나요?", a: "월간 크레딧은 각 청구 주기 말에 만료됩니다. 이는 모두에게 공정한 가격을 유지합니다. 정기적으로 더 필요하시면 플랜을 업그레이드하거나 탑업 팩을 구매하세요 — 탑업 크레딧은 만료되지 않습니다." },
        { q: "플랜 변경이나 언제든 취소가 가능한가요?", a: "예. 즉시 업그레이드, 청구 주기 말에 다운그레이드. 장기 계약 없음, 취소 수수료 없음. 연간 플랜은 미사용 월에 대한 비례 환불로 취소 가능합니다." },
        { q: "디자인 라이선스에 실제로 무엇이 포함되나요?", a: "모든 라이선스에 프리엔지니어드 CAD 파일(생산에 직접 라우팅 — 파일을 직접 다루지 않음), 선택한 티어의 완전한 상업적 권리(스탠다드, 독점 또는 Vault), Essentials Kit(카탈로그 촬영, 스펙, SEO 카피)이 포함됩니다. 라이프스타일 포토그래피가 포함된 Caratzon Kit은 Studio 크레딧으로 생성됩니다." },
        { q: "Vault 라이선싱(IP 양도)은 어떻게 작동하나요?", a: "Atelier 및 Enterprise 플랜에서 이용 가능합니다. Vault는 원본 CAD 파일을 포함한 디자인의 영구적 독점 소유권을 부여합니다. 디자이너는 일시불 매입 수수료를 받습니다. Vault 처리 후 디자인은 공개 카탈로그에서 제거됩니다. 당신의 것입니다." },
        { q: "제조를 직접 처리해야 하나요?", a: "아닙니다. 모든 생산 주문은 검증된 EPH 제조 네트워크를 통해 라우팅됩니다. 캐스팅·세팅·QC·물류가 엔드투엔드로 관리됩니다. 대시보드에서 마일스톤을 승인합니다 — 나머지는 저희가 처리합니다." },
      ],
    },
    bottomCta: { heading: "다르게 소싱할 준비가 되셨나요?", body: "Discover로 무료 시작. 준비되시면 업그레이드 — 약정 없음.", button: "카탈로그 보기", link: "맞춤형 플랜이 필요하신가요? 상담하세요." },
  },

  // ─── AI page ────────────────────────────────────────────────────────────
  ai: {
    meta: { title: "Cara AI — 주얼리 엔지니어링 인텔리전스 · Caratzon", description: "Cara는 공장 현장을 위해 설계하는 주얼리 엔지니어링 AI입니다. 수축 보정, 구조 분석, 부품표, 프로덕션 레디 콘셉트 — 판타지 렌더가 아닙니다.", socialDescription: "실제 제조를 위한 주얼리 엔지니어링 AI. 수축, 공차, 캐스팅 메커닉스, 착용감 — 모든 콘셉트가 프로덕션 레디." },
    hero: { headingLine1: "제너레이티브 이미지 도구가 아닙니다.", headingLine2: "주얼리 엔지니어링 AI입니다.", ctaDemo: "Cara AI 체험하기 — 라이브 데모", ctaCredits: "크레딧 비용 보기" },
    pillars: { label: "엔지니어링 제약 조건", headingLine1: "모든 콘셉트는 스트레스 테스트를", headingLine2: "거친 후 화면을 떠납니다." },
    capabilities: { label: "역량", headingLine1: "냅킨 스케치에서", headingLine2: "프로덕션 레디 블루프린트까지." },
    whatYouCanAsk: {
      label: "질문할 수 있는 것들",
      heading: "프롬프트 박스가 아닌, 대화입니다.",
      groups: [
        { title: "디자인 상담", prompts: ["\"이 스케치의 제조 가능성을 분석해 주세요\"", "\"18K 화이트 골드의 최소 벽두께는?\"", "\"이 헤일로 세팅이 일상 착용에 견딜까요?\""] },
        { title: "소재 과학", prompts: ["\"이 밴드 프로파일에 플래티넘 950 vs 18K 비교\"", "\"0.8mm 프롱에 어떤 합금을 써야 하나요?\"", "\"14K 옐로우 로스트 왁스 캐스팅 수축률은?\""] },
        { title: "비주얼 생성", prompts: ["\"이 콘셉트를 로즈 골드 파베 밴드로 렌더링\"", "\"아르데코 약혼 반지 무드보드 생성\"", "\"이 디자인의 360° 턴테이블 비디오 생성\""] },
        { title: "생산 계획", prompts: ["\"이 반지의 부품표 생성\"", "\"VS1 다이아몬드 18K로 제조 비용 추정\"", "\"50유닛 생산의 타임라인은?\""] },
      ],
    },
    creditsTeaser: { label: "Studio 크레딧", heading: "투명한 고정 비용 AI.", body: "모든 Cara 작업은 고정 크레딧 비용 — 메시지 5크레딧, 이미지 렌더 15크레딧, 360° 비디오 25크레딧. 토큰 미터링 없음, 놀라움 없음.", link: "전체 크레딧 비용 & 탑업 팩 보기 →" },
    finalCta: { heading: "직접 확인하세요.", body: "라이브 데모를 체험하세요 — 계정 불필요. 스케치를 업로드하거나 질문하고 Cara가 프로덕션 레디 콘셉트를 엔지니어링하는 것을 지켜보세요.", ctaDemo: "Cara AI 체험하기 — 라이브 데모", ctaPricing: "요금제 보기" },
  },

  // ─── Designer AI page ──────────────────────────────────────────────────
  designerAi: {
    meta: { title: "디자이너를 위한 Cara AI — Caratzon", description: "제조 가능성 CAD 검증, 포트폴리오 렌더, 360° 비디오, 룩북 포토그래피 생성 — 모두 Cara AI 기반. 디자인에 집중하세요; 엔지니어링은 Cara에게.", socialDescription: "주얼리 디자이너를 위한 AI 코파일럿. CAD 검증, 렌더 생성, 포트폴리오 구축 — 첫날부터 프로덕션 레디." },
    hero: { overline: "Cara AI · 디자이너를 위해", headingLine1: "두려움 없이 디자인하세요.", headingLine2: "엔지니어링은 Cara가 처리합니다.", body: "Cara는 당신의 AI 코파일럿입니다 — 마스터 주얼러이자 메커니컬 엔지니어. 제조 가능성을 위해 CAD를 검증하고, 스튜디오 퀄리티 렌더를 생성하며, 바이어가 연락하게 만드는 포트폴리오를 구축합니다. 당신은 크리에이티브 작업에 집중하세요.", ctaDemo: "Cara AI 체험하기 — 라이브 데모", ctaStudio: "Studio 열기" },
    pillars: {
      label: "Cara가 해 주는 일", headingLine1: "CAD 파일에서", headingLine2: "프로덕션 레디 포트폴리오까지.",
      items: [
        { stat: "CAD → 렌더", label: "포트폴리오 생성", body: ".3DM 또는 .STL을 업로드하면 Cara가 스튜디오 퀄리티 상품 촬영, 라이프스타일 렌더, 360° 턴테이블 비디오를 생성합니다 — 피스당 수천 달러가 들던 수준의 비주얼." },
        { stat: "±0.15mm", label: "제조 가능성 점검", body: "Cara가 실제 캐스팅 공차에 대해 디자인을 분석합니다 — 벽두께, 수축 보정, 프롱 강도, 에지 반경 — 바이어가 보기 전에 문제를 플래그합니다." },
        { stat: "자동 BOM", label: "부품표", body: "즉각적이고 정확한 부품표를 얻으세요 — 금속 중량, 스톤 수량, 합금별 비용 — 자신 있게 가격을 책정하고 바이어가 투명한 생산 견적을 볼 수 있습니다." },
        { stat: "당신의 스토리", label: "AI 스토리텔링", body: "Cara가 각 피스와 컬렉션의 에디토리얼 내러티브를 작성합니다. 일반 카피가 아닌 — 당신의 디자인 철학, 소재, 크리에이티브 의도에서 추출한 스토리." },
      ],
    },
    capabilities: {
      label: "디자이너 역량", headingLine1: "파일에서 피처드까지", headingLine2: "필요한 모든 것.",
      items: [
        { label: "CAD 검증", detail: "EPH 제조 공차에 대한 구조 분석 — 벽두께, 언더컷, 드래프트 각도" },
        { label: "상품 렌더", detail: "다양한 각도, 금속, 스톤 구성의 스튜디오 퀄리티 상품 촬영" },
        { label: "360° 턴테이블 비디오", detail: "프레젠테이션과 피치 덱에 삽입 가능한 애니메이션 턴테이블 미리보기" },
        { label: "룩북 포토그래피", detail: "브랜드 미학에 맞춘 AI 생성 라이프스타일 & 온모델 포토그래피" },
        { label: "컬렉션 내러티브", detail: "디자인을 컬렉션으로 그룹핑하면 Cara가 모든 피스에 걸친 통합 에디토리얼 스토리를 작성" },
        { label: "소재 인텔리전스", detail: "10K–24K 골드, 925 실버, 950 플래티넘의 합금별 가이던스 — 수축, 경도, 피니시" },
      ],
    },
    workflow: {
      label: "Cara와의 워크플로", heading: "업로드. 검증. 공개.",
      steps: [
        { title: "CAD 업로드", body: ".3DM, .STL 또는 .STEP 파일을 Studio에 드롭하세요. Cara가 지오메트리를 읽고 즉시 분석을 시작합니다." },
        { title: "리뷰 & 개선", body: "Cara가 제조 가능성 문제를 플래그하고 수정을 제안하며 전체 부품표를 생성합니다. Cara와 채팅하여 반복하세요 — 합금, 공차, 대안 세팅에 대해 질문하세요." },
        { title: "포트폴리오 생성", body: "원클릭으로 상품 렌더, 라이프스타일 촬영, 360° 비디오, 에디토리얼 스토리가 생성됩니다. 디자인이 원본 파일에서 바이어 준비 쇼케이스로 변환됩니다." },
        { title: "Inspire & 카탈로그에 공개", body: "검증·렌더 완료 디자인을 Inspire 크리에이터 페이지와 트레이드 카탈로그에 한 번에 푸시합니다. 바이어가 비주얼과 내러티브가 이미 준비된 상태로 작품을 발견합니다 — 라이선스 준비 완료." },
      ],
    },
    closing: "Cara는 당신의 창의성을 대체하지 않습니다 — 디자인과 그것을 봐야 할 바이어 사이의 모든 장벽을 제거합니다. 포토그래퍼 없이. 카피라이터 없이. 공장 중개인 없이. 오직 당신의 비전이 프로덕션 검증되고 아름답게 프레젠테이션됩니다.",
    creditsTeaser: { label: "Studio 크레딧", heading: "고정 비용 AI. 놀라움 없이.", body: "모든 Cara 작업에 투명한 크레딧 비용 — 메시지 5크레딧, 렌더 15크레딧, 360° 비디오 25크레딧. 크레딧은 플랜에 포함되며, 탑업은 언제든 가능.", link: "크레딧 비용 보기 →" },
    finalCta: { heading: "당신의 디자인은 주목받을 자격이 있습니다.", body: "첫 CAD를 업로드하고, Cara가 검증·렌더한 후, Inspire에 공개하세요 — 모두 5분 이내에.", ctaDemo: "Cara AI 체험하기 — 라이브 데모", ctaStudio: "Studio 열기" },
  },

  // ─── AI Credits page ────────────────────────────────────────────────────
  aiCredits: {
    meta: { title: "Studio 크레딧 — Caratzon", description: "Caratzon의 Studio 크레딧 작동 방식 — 포토 키트, 디자인 상담 등을 위한 고정 비용 AI 작업. 이미지당 수수료 없음.", socialDescription: "포토 키트, 디자인 상담 등을 위한 고정 비용 AI 작업." },
    hero: { label: "Studio 크레딧", heading: "투명한 고정 비용 AI.", body: "Caratzon의 모든 AI 기반 작업은 고정 Studio 크레딧 수를 사용합니다. 이미지당 수수료 없음, 토큰 미터링 없음, 놀라움 없음." },
    howItWorks: {
      label: "이용 방법", heading: "획득, 사용, 충전.",
      steps: [
        { step: "01", title: "크레딧 획득", body: "유료 플랜에 월간 크레딧 할당량이 포함됩니다. Explorer 계정은 200 웰컴 크레딧으로 체험 가능." },
        { step: "02", title: "모든 작업에 사용", body: "포토 키트, 룩북, Cara AI 상담 — 각 작업에 사전 표시된 고정 크레딧 비용." },
        { step: "03", title: "더 필요할 때 충전", body: "크레딧 팩을 즉시 구매. 탑업 크레딧은 만료되지 않습니다 — 구독 크레딧은 각 청구 주기에 리셋." },
      ],
    },
    photoStudio: { label: "포토 스튜디오", heading: "프로덕션 레디 에셋.", body: "디자인을 업로드하고 카탈로그 레디 포토그래피, 라인시트, 마케팅 카피를 얻으세요 — 모두 AI가 수 분 내에 생성." },
    caraAi: { label: "Cara AI", heading: "당신의 디자인 엔지니어링 파트너.", body: "Cara는 야금학, 스톤 세팅, 구조적 건전성, 제조 제약 조건을 이해하는 AI 주얼리 엔지니어입니다. 무엇이든 물어보세요 — 소재 과학부터 BOM 추정까지.", note: "텍스트 메시지(5크레딧)는 전송 시 과금됩니다. 이미지 및 비디오 도구는 AI가 호출할 때 과금 — 실행 전 비용이 표시됩니다. 렌더 몇 개가 포함된 10메시지 세션은 약 100–150 크레딧을 사용합니다." },
    monthlyAllowances: {
      label: "월간 할당량", heading: "플랜에 포함된 크레딧.",
      tableHeaders: ["플랜", "월간 크레딧", "웰컴 크레딧", "가격"],
      rows: [
        { plan: "Discover", monthly: "—", welcome: "200", price: "무료" },
        { plan: "Source", monthly: "1,500", welcome: "—", price: "$149/월" },
        { plan: "Atelier", monthly: "5,000", welcome: "—", price: "$399/월" },
        { plan: "Enterprise", monthly: "맞춤형", welcome: "—", price: "맞춤형" },
      ],
      note: "구독 크레딧은 각 청구 주기에 리셋 — 미사용 크레딧은 이월되지 않습니다. 탑업 크레딧은 만료되지 않습니다.",
    },
    topUpPacks: { label: "탑업 팩", heading: "추가 구매, 영구 보유.", body: "대규모 컬렉션 런칭? 크레딧을 즉시 구매하세요 — 만료되지 않습니다.", bestValue: "최고 가치", creditsLabel: "크레딧", perCredit: "¢/크레딧", buyPrefix: "구매", buySuffix: "크레딧", note: "구독이 항상 최고의 딜입니다 — 구독 크레딧은 탑업보다 크레딧당 비용이 낮습니다.", compareLink: "플랜 비교" },
    faq: {
      label: "자주 묻는 질문", heading: "크레딧, 설명.",
      items: [
        { q: "Cara와 대화 중 크레딧이 소진되면?", a: "텍스트 메시지는 전송 전에 확인됩니다 — 탑업 안내가 표시됩니다. 이미지 및 비디오 도구는 생성 전에 확인하므로, AI가 고비용 작업 실행 전 더 많은 크레딧이 필요한지 알려줍니다." },
        { q: "미사용 구독 크레딧이 이월되나요?", a: "아닙니다. 구독 크레딧은 각 청구 주기 시작 시 리셋됩니다. 정기적으로 더 필요하시면 플랜 업그레이드 또는 탑업 팩 구매를 고려하세요 — 탑업 크레딧은 만료되지 않습니다." },
        { q: "Cara 메시지 한 건이 5크레딧 이상 들 수 있나요?", a: "예. 기본 메시지 비용은 5크레딧이지만, Cara가 응답 중 이미지(각 15)나 비디오(각 25)를 생성하면 점진적으로 과금됩니다. 렌더 2-3개가 포함된 일반적 디자인 상담은 총 약 50-70 크레딧입니다." },
        { q: "관리자 계정도 크레딧이 과금되나요?", a: "아닙니다. 관리자 계정은 내부 테스트 및 운영을 위해 크레딧 과금에서 면제됩니다." },
        { q: "Essentials Kit과 Caratzon Kit의 차이는?", a: "Essentials Kit(10크레딧)은 카탈로그 촬영, 스펙, SEO 카피를 생성합니다. Caratzon Kit(100크레딧)은 Essentials의 모든 것에 더해 라이프스타일 포토그래피, 에디토리얼 이미지, 소셜 레디 에셋이 포함된 풀 룩북을 포함합니다." },
      ],
    },
    actions: {
      photoStudio: [
        { action: "Essentials Kit", detail: "카탈로그 촬영, 스펙 & SEO 카피" },
        { action: "Caratzon Kit", detail: "풀 포토 스튜디오 — 라이프스타일 포토그래피, 라인시트 & SNS 에셋" },
        { action: "단일 포토 재생성", detail: "마음에 들지 않는 사진 1장 재생성" },
        { action: "룩북 PDF", detail: "브랜디드 도매 라인시트" },
      ],
      caraAi: [
        { action: "메시지", detail: "스케치 분석, Q&A, 소재 과학, 구조 리뷰 또는 디자인 상담" },
        { action: "스케치", detail: "텍스트 설명으로 빠른 콘셉트 스케치" },
        { action: "이미지 렌더", detail: "충실한 스케치→콘셉트 렌더, 무드보드, 카탈로그 촬영 또는 디자인 조정" },
        { action: "룩북 이미지", detail: "프레젠테이션 덱용 에디토리얼 룩북 이미지" },
        { action: "360° 비디오", detail: "모든 각도에서 디자인의 턴테이블 비디오" },
      ],
    },
  },

  demo: {
    meta: { title: "Cara AI 데모 · Caratzon", description: "Cara를 체험하세요 — 공장 현장과 착용감을 위해 설계하는 주얼리 엔지니어링 AI. 수축, 공차, 캐스팅 메커닉스, 착용감 — 모든 콘셉트가 프로덕션 레디. 라이브 데모." },
    splash: { eyebrow: "주얼리 엔지니어링 AI", heading: "Cara를 만나보세요", subheading: "공장이 만들 수 있고 고객이 착용하고 싶은 주얼리를 디자인하는 엔지니어링 AI — 첫 스케치부터.", cta: "디자인 시작", enterHint: "{key}를 눌러 시작", promptDivider: "또는 프롬프트 사용", signIn: "로그인", noAccount: "계정 불필요", notSaved: "대화가 저장되지 않습니다", fullAccess: "풀 플랫폼 접근 →", namePlaceholder: "이름 (갤러리에 표시)", nameHint: "작품에 크레딧하기 위해" },
    prompts: [
      { label: "약혼 반지", text: "18k 로즈 골드에 1ct 오벌 다이아몬드를 사용한 모던 솔리테어 약혼 반지를 디자인하세요" },
      { label: "아르데코 칵테일 링", text: "에메랄드와 오닉스를 사용한 아르데코 기하학 영감의 볼드한 칵테일 링을 만드세요" },
      { label: "테니스 브레이슬릿", text: "플래티넘에 사파이어와 다이아몬드가 번갈아 세팅된 미니멀 테니스 브레이슬릿을 디자인하세요" },
      { label: "스컬프처럴 커프", text: "유기적인 흐르는 라인의 스컬프처럴 골드 커프를 원합니다, 스톤 없이 순수 금속 작업" },
    ],
    capabilities: [
      { label: "스케치에서 프로덕션 블루프린트로", desc: "냅킨 스케치 업로드 — Cara가 공장 현장에 맞게 엔지니어링" },
      { label: "캐스팅 & 수축 분석", desc: "금속별 수축 보정, 벽두께 검증" },
      { label: "스톤 공차 매핑", desc: "프롱 배치, 시트 깊이, 거들 클리어런스 — 엔지니어링" },
      { label: "착용감 & 편안함", desc: "밴드 프로파일, 무게 밸런스, 에지 반경 — 종일 착용 설계" },
    ],
    header: { brandLabel: "Cara AI", tabs: { create: "크리에이트", gallery: "갤러리" }, back: "← 뒤로", fullAccess: "풀 접근 얻기" },
    gallery: {
      loading: "갤러리 로딩 중…", emptyTitle: "아직 디자인이 없습니다", emptyBody: "처음으로 무언가를 만들어 보세요. 이 데모 세션에서 생성된 디자인이 모두에게 표시됩니다.", emptyAction: "첫 디자인 만들기", sectionTitle: "이 이벤트에서 생성됨", countSingular: "데모 방문자가 생성한 {count}개 디자인", countPlural: "데모 방문자가 생성한 {count}개 디자인", createYours: "당신의 것을 만드세요", createdWith: "Cara AI로 제작됨", generating: "스토리 생성 중…", viewStory: "스토리 보기",
      toolLabels: { render: "AI 렌더", mood_board: "무드보드", catalog_render: "카탈로그 렌더" }, fallbackTool: "렌더",
      timeAgo: { justNow: "방금", minutes: "{n}분 전", hours: "{n}시간 전", days: "{n}일 전" },
    },
    story: {
      backToGallery: "← 갤러리로 돌아가기", fromSketch: "스케치에서", theSketch: "스케치", sketchCaption: "{name}의 원본 스케치", theDetails: "디테일", detailType: "유형", detailMetal: "금속", detailStyle: "스타일", detailStones: "스톤", detailFinish: "피니시", detailWeight: "중량", detailPrice: "예상 가격", structuralAnalysis: "구조 분석", theStory: "스토리", showcase: "쇼케이스", designJourney: "디자인 여정", theConversation: "대화", moreMessages: "이 대화의 {n}개 추가 메시지", createdBy: "만든 이", createdWith: "{event}에서 Cara AI로 제작됨", eventName: "라이브 데모", tryIt: "Cara 직접 체험하기 →",
      artifactLabels: { render: "디자인 렌더", catalog_render: "카탈로그 렌더", concept_sketch: "콘셉트 스케치", mood_board: "무드보드", structural_analysis: "구조 분석", material_recommendation: "소재 분석", bom: "부품표", sketch_analysis: "스케치 분석", lookbook: "룩북", video: "턴테이블 비디오", inspiration: "인스피레이션 분석", design_specs: "디자인 스펙", finalize_design: "최종 디자인", gallery_share: "갤러리 공유" },
    },
    brands: {
      splash: { eyebrow: "브랜드를 위한 디자인 인텔리전스", subheading: "비전을 설명하세요. 렌더링된 결과를 보세요. 생산에 보내세요.", cta: "탐색 시작", namePlaceholder: "이름 또는 브랜드명", nameHint: "경험을 개인화하기 위해", fullAccess: "트레이드 계정 시작 →" },
      prompts: [
        { label: "커스텀 테니스 브레이슬릿", text: "봄 컬렉션을 위해 이 테니스 브레이슬릿을 로즈 골드로 커스터마이즈하세요" },
        { label: "시그니처 펜던트", text: "브랜드의 기하학적 모티프를 활용한 시그니처 펜던트 — 미니멀, 14k 골드" },
        { label: "웨딩 밴드 옵션", text: "3가지 가격대의 미니멀 웨딩 밴드 세트 옵션을 보여주세요" },
        { label: "스케치에서 생산으로", text: "커프 스케치가 있는데 — 프로덕션 레디로 만들어줄 수 있나요?" },
      ],
      capabilities: [
        { label: "커스터마이징 미리보기", desc: "포토리얼리스틱 디테일로 렌더링된 디자인 비전 확인" },
        { label: "소재 & 피니시 옵션", desc: "금속, 스톤, 피니시를 나란히 탐색" },
        { label: "샘플 가격", desc: "프로토타이핑과 소규모 생산의 즉시 견적" },
        { label: "프로덕션 타임라인", desc: "콘셉트에서 배송까지 — 전체 제조 경로 확인" },
      ],
      header: { fullAccess: "트레이드 계정 시작" },
    },
  },

  // ─── Production Dashboard ─────────────────────────────────────────────────
  productionDashboard: {
    title: "프로덕션 대시보드",
    tabs: { portfolio: "포트폴리오", board: "보드" },
    zoom: "줌", day: "일", week: "주", month: "월",
    search: "검색...", searchPlaceholder: "플랜, 디자인, 제조사 검색...",
    keyboardShortcuts: "키보드 단축키", groupBy: "그룹화", status: "상태", phase: "단계",
    dragDisabledInPhase: "단계 뷰에서 드래그 비활성화", backToPortfolio: "포트폴리오",
    subOrders: "하위 주문", unassigned: "미할당", noItems: "항목 없음",
    densityCompact: "컴팩트 보기", densityComfortable: "편안한 보기",
    empty: {
      portfolioTitle: "활성 생산 없음", portfolioDescription: "프로덕션 플래너에서 생성된 플랜이 승인 후 여기에 표시됩니다.", portfolioLink: "프로덕션 플래너 열기",
      boardTitle: "하위 주문 없음", boardDescription: "필터에 일치하는 하위 주문이 없습니다.", boardLink: "포트폴리오 보기",
      planTitle: "하위 주문 없음", planDescription: "이 플랜에 아직 하위 주문이 없습니다.", planLink: "프로덕션 플래너 열기",
    },
    filters: { allStatuses: "전체 상태", allDesigners: "전체 디자이너", allBuyers: "전체 바이어" },
    command: { noResults: "결과가 없습니다.", productionPlans: "프로덕션 플랜", quickActions: "빠른 작업", goToPortfolio: "포트폴리오로 이동", goToBoard: "보드로 이동", navigate: "이동", open: "열기", close: "닫기" },
    kanban: {
      blockedAndCancelled: "차단 & 취소됨",
      columns: { PENDING: "대기", QUOTED: "견적", ASSIGNED: "할당", IN_PROGRESS: "진행 중", QA: "QA", COMPLETED: "완료", BLOCKED: "차단됨", CANCELLED: "취소됨" },
    },
    gantt: { today: "오늘", criticalPath: "크리티컬 패스", duration: "기간", days: "일", manufacturer: "제조사", dependsOn: "의존" },
    detail: { status: "상태", manufacturer: "제조사", duration: "기간", dependencies: "의존성" },
    hover: { duration: "기간", manufacturer: "제조사" },
  },

  // ─── Buyer Milestone Tracker ──────────────────────────────────────────────
  buyerTracker: {
    title: "생산 진행 현황",
    inProgress: "진행 중",
    pending: "대기",
    milestones: {
      orderConfirmed: "주문 확인",
      designEngineering: "디자인 엔지니어링",
      sampleProduction: "샘플 생산",
      approval: "승인",
      manufacturing: "제조",
      finalFinishing: "최종 피니싱",
      qualityCheckDelivery: "품질 검사 & 배송",
    },
  },
};
